import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import Editscreen from "../../../../blinkscribe/archeivescreens/editscreen";
import { useSelector, useDispatch } from "react-redux";
import { setConfig } from "../../../../reduxtoolkit/userconfigslice";
import api from "../../../../api/api";
import careiqapi from "../../../../api/careiqapi";
import blinkscribeapi from "../../../../api/blinkscribeapi";
import dayjs from "dayjs";
import moment from "moment";
import ProcedureDocument from "./ProcedureDocument";
import { useRecording } from "../../../../context/RecordingContext";
import { htmlNoteToText, splitApEntries, parseApEntry } from "../../../../utils/apnote";
import { eventBus } from "../../../../utils/eventBus";
import { imagingIdSet, isImagingReportTwin } from "../Document/documentTwins";
import LineChart from "./trendslinegraph";
import ClinicalEncounterDashboard from "../priorvisits/ClinicalEncounterDashboard";
import { withRetry, applyListAnswer, documentsAsOfLine, docListUrl } from "./careiqFetch";
import "./CareIQTab.css";
import "../../frames/chartframe.css";
import {
    CalendarDays, Check, ChevronDown, ClipboardList, History, Lightbulb, ListChecks, Pencil,
    Plus, Save, Search, Settings, Sparkles, Stethoscope, Trash2, TriangleAlert, X,
} from "lucide-react";
import { getPracticeId } from "../../../../config/practice";
import { fetchRemainingPages } from "./documentPages";
import { visitPillLabel } from "./N3/televisit";

const practiceId = getPracticeId();

/* ─── Design Tokens (coral brand palette) ─── */
const BRAND = {
    coral: "#E8653A", coralLight: "#FFF3EF", coralMid: "#F4A68A",
    navy: "#1B2A4A", navyLight: "#2D4166",
    slate: "#64748B", slateMuted: "#94A3B8",
    emerald: "#10B981", emeraldLight: "#D1FAE5",
    amber: "#F59E0B", amberLight: "#FEF3C7",
    red: "#EF4444", redLight: "#FEE2E2",
    purple: "#8B5CF6", purpleLight: "#EDE9FE",
    blue: "#3B82F6", blueLight: "#DBEAFE",
    surface: "#FFFFFF", surfaceMuted: "#F8FAFC",
    border: "#E2E8F0", borderLight: "#F1F5F9",
};

/* SVG Icon Components */
const SparklesIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        <path d="M5 3v4" /><path d="M19 17v4" /><path d="M3 5h4" /><path d="M17 19h4" />
    </svg>
);
const AlertTriangleIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
        <path d="M12 9v4" /><path d="M12 17h.01" />
    </svg>
);
const FileTextIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" />
    </svg>
);
const ChevronRightIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m9 18 6-6-6-6" />
    </svg>
);
const GearIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

/* ─── Chart timeline document-type preferences ───
   Effective set = user override (user_master.config.timeline_docs, via the ⚙ modal)
   ?? practice default (practice_feature 'timeline' config, set at onboarding)
   ?? built-in default (imaging + clinical docs — the pre-preferences behavior). */
const TIMELINE_DOC_OPTIONS = [
    { key: "imagingresult", label: "Imaging results", hint: "X-ray, MRI, CT, ultrasound reports" },
    { key: "clinicaldocument", label: "Clinical documents", hint: "discharge summaries, referrals, outside records" },
    { key: "encounterdocument", label: "Progress / encounter notes", hint: "in-office visit notes (can be noisy)" },
];
const DEFAULT_TIMELINE_DOCS = ["imagingresult", "clinicaldocument"];
// The two document lists the base fetch loads, with their response wrap keys.
const BASE_DOC_LISTS = [["imagingresult", "imagingresults"], ["clinicaldocument", "clinicaldocuments"]];
const NO_BASE_DOCS = { imagingresult: [], clinicaldocument: [] };
const CheckCircleIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" />
    </svg>
);
const PencilIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" />
    </svg>
);
const TrashIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    </svg>
);
const XIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
);
const SaveIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
        <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" /><path d="M7 3v4a1 1 0 0 0 1 1h7" />
    </svg>
);

/* DxPill component */
const DxPill = ({ code, label, onAction, actionLabel }) => (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
        <div style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            padding: "3px 8px 3px 5px", background: BRAND.surfaceMuted,
            borderRadius: "5px", border: `1px solid ${BRAND.border}`,
        }}>
            {code && <span style={{
                fontSize: "9px", fontWeight: 700, color: BRAND.slate,
                background: BRAND.borderLight, borderRadius: "3px", padding: "1px 4px",
                letterSpacing: "0.02em", fontFamily: "'DM Mono', monospace",
            }}>{code}</span>}
            <span style={{ fontSize: "10.5px", fontWeight: 500, color: BRAND.navy, lineHeight: 1.2 }}>{label}</span>
        </div>
        {onAction && (
            <button
                onClick={onAction} title={actionLabel || "Add"}
                style={{
                    width: "20px", height: "20px", borderRadius: "50%",
                    background: `${BRAND.emerald}18`, border: `1px solid ${BRAND.emerald}40`,
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    color: BRAND.emerald, fontSize: "14px", fontWeight: 700, lineHeight: 1, padding: 0,
                    transition: "all 0.15s ease",
                }}
            >{actionLabel === "✓" ? "✓" : "+"}</button>
        )}
    </div>
);

/* ICD-10 code pattern: letter followed by digits/dots (e.g. J45.20, E11.9) */
const ICD10_PATTERN = /^[A-Z]\d{2}(\.\d{1,4})?$/i;

/* ─── Helpers ─── */
const statusClass = (s) => {
    if (!s) return "";
    const l = s.toLowerCase();
    if (l === "abnormal" || l === "critical") return "abnormal";
    if (l === "high") return "high";
    if (l === "low") return "low";
    if (l === "overweight") return "overweight";
    return "normal";
};

const pillClass = (type) => {
    if (type === "visit") return "visit";
    if (type === "lab") return "lab";
    if (type === "imaging") return "imaging";
    if (type === "document") return "imaging"; // share imaging styling for now
    return "visit";
};

const pillLabel = (type) => {
    if (type === "visit") return "Visit";
    if (type === "lab") return "Lab";
    if (type === "imaging") return "Imaging";
    if (type === "document") return "DOC";
    return type;
};

/* ═══ Visit Summary Modal (Tabbed: BlinkScribe + Encounter Summary) ═══ */
const VisitSummaryModal = ({ encounter, patientId, patientInfo, onClose }) => {
    // BS-only synthetic visits (no Athena encounter) open on the tab that
    // actually has content.
    // BlinkScribe (the Edit interface) is the default tab for every visit
    // opened from the timeline (user request 2026-09-01).
    const [activeTab, setActiveTab] = useState("blinkscribe");
    const [bsLoading, setBsLoading] = useState(false);
    const [bsRecord, setBsRecord] = useState(null);
    const [bsError, setBsError] = useState(false);
    // Full note content — recording/details returns METADATA ONLY (no
    // subjective/hpi/assessment fields), so the tab rendered "Note content
    // not yet available" for every visit until this second fetch was added
    // (QA 2026-08-19, pt 13795).
    const [bsNote, setBsNote] = useState(null);
    const [esLoading, setEsLoading] = useState(false);
    const [esData, setEsData] = useState(null);
    const [esError, setEsError] = useState(false);
    // Handwritten Note state
    const [hwLoading, setHwLoading] = useState(false);
    const [hwPages, setHwPages] = useState(null); // null = not fetched, [] = empty
    const [hwError, setHwError] = useState(false);
    const [hwDocDesc, setHwDocDesc] = useState("");
    const [hwMatch, setHwMatch] = useState(undefined); // undefined = checking, null = no match, object = match found
    // Order History state
    const [ohLoading, setOhLoading] = useState(false);
    const [ohOrders, setOhOrders] = useState(null); // null = not fetched
    const [ohError, setOhError] = useState(false);
    const [ohClaims, setOhClaims] = useState(null);
    const userConfig = useSelector((state) => state.userconfig);
    const deptId = userConfig?.config?.departmentid || 1;

    const visitDate = encounter.startdate || encounter.date || encounter.encounterdate;
    const encounterId = encounter.encounterid;

    /* Fetch BlinkScribe note for the visit date */
    useEffect(() => {
        if (!patientId || !visitDate) return;
        let cancelled = false;
        const fetchBS = async () => {
            setBsLoading(true);
            setBsError(false);
            setBsNote(null);
            try {
                const dateStr = dayjs(visitDate).format("YYYY-MM-DD");
                const res = await blinkscribeapi.post(
                    `/v1/patient/${patientId}/recording/details`,
                    { filter_array: { date_filter: { start_date: dateStr, end_date: dateStr } } }
                );
                const data = res?.data || res;
                const rec = Array.isArray(data) && data.length > 0 ? data[0] : null;
                if (!cancelled) setBsRecord(rec);
                if (rec?.id) {
                    try {
                        const noteRes = await blinkscribeapi.get(`/v1/recording/note/${rec.id}/get`);
                        const row = (noteRes?.data && noteRes.data[0]) || (Array.isArray(noteRes) ? noteRes[0] : null);
                        let nj = row?.note_json;
                        if (typeof nj === "string") { try { nj = JSON.parse(nj); } catch { nj = null; } }
                        if (nj && nj.soap_note && typeof nj.soap_note === "object") nj = { ...nj.soap_note, ...nj };
                        if (!cancelled) setBsNote(nj && typeof nj === "object" ? nj : null);
                    } catch { /* fall back to the metadata-only view */ }
                }
            } catch {
                if (!cancelled) setBsError(true);
            } finally {
                if (!cancelled) setBsLoading(false);
            }
        };
        fetchBS();
        return () => { cancelled = true; };
    }, [patientId, visitDate]);

    /* Fetch Encounter Summary (structured data) */
    useEffect(() => {
        if (!encounterId) return;
        if (esData) return; // already fetched
        let cancelled = false;
        const fetchES = async () => {
            setEsLoading(true);
            setEsError(false);
            try {
                const res = await careiqapi.post(
                    `/v1/fhir-mirror/${practiceId}/encounter/specific-summary`,
                    { encounter_id: encounterId, patient_id: parseInt(patientId) || 0 }
                );
                if (!cancelled) {
                    const raw = res?.data?.data || res?.data || res;
                    setEsData(raw);
                }
            } catch {
                if (!cancelled) setEsError(true);
            } finally {
                if (!cancelled) setEsLoading(false);
            }
        };
        fetchES();
        return () => { cancelled = true; };
    }, [encounterId, activeTab, esData]);

    /* Pre-check: fetch progress notes list on mount to know if handwritten note exists */
    useEffect(() => {
        if (!patientId || !visitDate) return;
        let cancelled = false;
        const checkHW = async () => {
            try {
                const visitDayjs = dayjs(visitDate);
                const listRes = await careiqapi.post(
                    `/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/get`,
                    { category: "ProgressNotes", department_id: deptId }
                );
                const resData = listRes?.data || listRes;
                const docs = resData?.encounterdocuments || resData?.data || [];
                // Filter out non-handwritten documents (EKGs, imaging, etc.)
                const isHandwrittenNote = (d) => {
                    const desc = (d.documentdescription || d.description || "").toLowerCase();
                    const excludePatterns = ["ekg", "ecg", "electrocardiogram", "imaging", "x-ray", "xray", "mri", "ct scan", "ultrasound", "lab"];
                    return !excludePatterns.some(p => desc.includes(p));
                };
                const filteredDocs = docs.filter(isHandwrittenNote);

                // Match by encounter ID first, then fall back to date
                let match = filteredDocs.find(d => {
                    const docEncId = String(d.encounterid || "");
                    const targetId = String(encounterId || "");
                    return docEncId && targetId && docEncId === targetId;
                });
                if (!match) {
                    const candidates = filteredDocs
                        .map(d => ({ ...d, _dt: dayjs(d.createddatetime || d.createddate) }))
                        .filter(d => {
                            const diff = d._dt.diff(visitDayjs, 'day');
                            return diff >= 0 && diff <= 7;
                        })
                        .sort((a, b) => a._dt.valueOf() - b._dt.valueOf());
                    match = candidates[0] || null;
                }
                if (!cancelled) setHwMatch(match || null);
            } catch {
                if (!cancelled) setHwMatch(null);
            }
        };
        checkHW();
        return () => { cancelled = true; };
    }, [patientId, visitDate]);

    /* Fetch Handwritten (Progress) Note content when tab is opened */
    useEffect(() => {
        if (activeTab !== "handwritten" || !patientId || !visitDate) return;
        if (hwPages !== null) return; // already fetched
        if (!hwMatch) return; // no matching doc
        let cancelled = false;
        const fetchHW = async () => {
            setHwLoading(true);
            setHwError(false);
            try {
                if (!cancelled) setHwDocDesc(hwMatch.documentdescription || "Progress Note");
                const docId = hwMatch.encounterdocumentid;
                const hwUrl = `/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/image-result`;
                const hwBody = {
                    patientid: patientId,
                    imagingresultid: docId,
                    fhir_resource_id: hwMatch.fhir_resource_id || null,
                    category: "ProgressNotes",
                };
                const contentRes = await careiqapi.post(hwUrl, { ...hwBody, page_offset: 0, page_limit: 20 });
                const contentData = contentRes?.data || contentRes;
                const firstEntry = Array.isArray(contentData) ? contentData[0] : contentData;
                const firstPages = firstEntry?.pages || [];
                // A note longer than the first 20 pages: the rest too (owner 10-07, "I can
                // only see 5"; review F7). Free: v2 cache / FHIR. No extra request otherwise.
                const pages = (Number(firstEntry?.total_pages) || 0) > firstPages.length
                    ? await fetchRemainingPages((u, b) => careiqapi.post(u, b), hwUrl, hwBody,
                        firstPages, firstEntry.total_pages, { isCancelled: () => cancelled })
                    : firstPages;
                if (!cancelled) setHwPages(pages);
            } catch (err) {
                console.error("[VisitModal] Handwritten note fetch error:", err);
                if (!cancelled) setHwError(true);
            } finally {
                if (!cancelled) setHwLoading(false);
            }
        };
        fetchHW();
        return () => { cancelled = true; };
    }, [activeTab, patientId, visitDate, hwPages, hwMatch]);


    /* Fetch Order History for the visit date */
    useEffect(() => {
        if (activeTab !== "orders" || !patientId || !visitDate) return;
        if (ohOrders !== null) return; // already fetched
        let cancelled = false;
        const fetchOH = async () => {
            setOhLoading(true);
            setOhError(false);
            try {
                const dateStr = dayjs(visitDate).format("YYYY-MM-DD");
                const res = await careiqapi.post(`/v1/${practiceId}/orders/get`, {
                    patientid: patientId,
                    orderdate: dateStr,
                });
                const d = res?.data || res;
                const orders = Array.isArray(d) ? d : (d?.data || d?.orders || []);
                if (!cancelled) { setOhOrders(orders); setOhLoading(false); }
                // Fire-and-forget claims fetch (simple DB read, no Athena call)
                careiqapi.get(`/v1/${practiceId}/claims/by-date?patientid=${patientId}&service_date=${dateStr}`)
                    .then(claimsRes => {
                        const cd = claimsRes?.data || claimsRes;
                        if (!cancelled) setOhClaims(cd?.data || []);
                    })
                    .catch(e => console.warn("[VisitModal] Claims:", e?.message));
            } catch (err) {
                console.error("[VisitModal] Order history fetch error:", err);
                if (!cancelled) { setOhError(true); setOhLoading(false); }
            }
        };
        fetchOH();
        return () => { cancelled = true; };
    }, [activeTab, patientId, visitDate, ohOrders]);

    const fields = [
        ["Date", visitDate],
        ["Type", encounter.appointmenttypename],
        ["Provider", encounter.providerfullname || encounter.providerid],
        ["Status", encounter.appointmentstatus || encounter.status],
    ];

    // Who actually produced the note (user request 2026-09-02): the
    // sign-off name wins, then the recorder, then the appointment's
    // scheduled provider. bsRecord loads async, so the chip appears as
    // soon as the recording fetch lands.
    const noteSigner = bsRecord?.signed_by_name || "";
    const noteRecorder = bsRecord?.recorded_by || "";
    const noteProvider = noteSigner || noteRecorder ||
        bsRecord?.providername || encounter.providerfullname || "";
    const noteProviderRole = noteSigner ? "signed off" : noteRecorder ? "recorded" : "";

    return (
        <div className="ciqt-modal-bg" onClick={onClose}>
            <div className="ciqt-modal ciqt-modal-lg" onClick={(e) => e.stopPropagation()}>
                {/* Header with visit info */}
                <div className="ciqt-modal-head">
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <span className="ciqt-modal-t">{encounter.appointmenttypename || "Visit Details"}</span>
                            {noteProvider && (
                                <span style={{
                                    background: "#EEF2FF", color: "#3730A3", fontWeight: 700,
                                    fontSize: 13, padding: "3px 12px", borderRadius: 999,
                                    whiteSpace: "nowrap", display: "inline-flex",
                                    alignItems: "center", gap: 6,
                                }}>
                                    🩺 {noteProvider}
                                    {noteProviderRole && (
                                        <span style={{ fontWeight: 500, color: "#6366F1", fontSize: 11 }}>
                                            {noteProviderRole}
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                            {visitDate ? dayjs(visitDate).format("MMMM D, YYYY") : ""}
                            {/* Recorder shown alongside when someone else signed off. */}
                            {noteSigner && noteRecorder && noteRecorder !== noteSigner
                                ? ` · Recorded by ${noteRecorder}` : ""}
                            {encounter.providerfullname && encounter.providerfullname !== noteProvider
                                ? ` · ${encounter.providerfullname}` : ""}
                        </div>
                    </div>
                    <button className="ciqt-modal-x" onClick={onClose}>✕</button>
                </div>

                {/* Tabs */}
                <div className="ciqt-visit-tabs">
                    <button
                        className={`ciqt-visit-tab ${activeTab === "blinkscribe" ? "ciqt-visit-tab-active" : ""}`}
                        onClick={() => setActiveTab("blinkscribe")}
                    >
                        📝 BlinkScribe Note
                    </button>
                    <button
                        className={`ciqt-visit-tab ${activeTab === "summary" ? "ciqt-visit-tab-active" : ""}`}
                        onClick={() => setActiveTab("summary")}
                    >
                        📋 Encounter Summary
                    </button>
                    {hwMatch !== null && (
                    <button
                        className={`ciqt-visit-tab ${activeTab === "handwritten" ? "ciqt-visit-tab-active" : ""}`}
                        onClick={() => setActiveTab("handwritten")}
                    >
                        ✍️ Handwritten Note
                    </button>
                    )}
                    <button
                        className={`ciqt-visit-tab ${activeTab === "orders" ? "ciqt-visit-tab-active" : ""}`}
                        onClick={() => setActiveTab("orders")}
                    >
                        📦 Order History
                    </button>
                </div>

                {/* Tab Content */}
                <div className="ciqt-modal-body" style={{ minHeight: 200 }}>
                    {activeTab === "blinkscribe" && (
                        <div>
                            {bsLoading ? (
                                <div className="ciqt-visit-loading">
                                    <div className="ciqt-spinner" />
                                    <span>Loading BlinkScribe note…</span>
                                </div>
                            ) : bsError ? (
                                <div className="ciqt-empty">Failed to load BlinkScribe note</div>
                            ) : bsRecord?.id ? (
                                /* The full Edit interface — the same one as the
                                   archive's Edit modal, rendered INLINE in this
                                   tab (embedded — no portaled overlay stacking a
                                   second dialog on top of the visit modal; user
                                   request 2026-09-02). Sign-off closes the visit
                                   modal entirely. */
                                <Editscreen
                                    selectedRecord={bsRecord}
                                    embedded
                                    setIsEditScreen={(v) => { if (!v) setActiveTab("summary"); }}
                                    onDone={onClose}
                                />
                            ) : (
                                <div className="ciqt-empty-state">
                                    <div className="ciqt-empty-icon">📝</div>
                                    <div className="ciqt-empty-title">No BlinkScribe Note</div>
                                    <div className="ciqt-empty-desc">No recording found for this visit date</div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "summary" && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                            {esLoading ? (
                                <div className="ciqt-visit-loading">
                                    <div className="ciqt-spinner" />
                                    <span>Loading encounter summary…</span>
                                </div>
                            ) : esError ? (
                                <div className="ciqt-empty">Failed to load encounter summary</div>
                            ) : esData ? (
                                <>
                                    {hwMatch && (
                                        <div style={{background:'#FFFBEB',border:'1px solid #FCD34D',color:'#92400E',
                                                     fontSize:'13px',padding:'6px 12px',borderRadius:'6px',margin:'6px 0'}}>
                                            This visit was documented as a handwritten note - structured sections
                                            (Chief Complaint, HPI, Assessment/Plan) may be blank here. Open the
                                            Handwritten Note tab for the full documentation.
                                        </div>
                                    )}
                                    <ClinicalEncounterDashboard rawData={esData} fallbackPatient={patientInfo} />
                                </>
                            ) : (
                                <div className="ciqt-empty-state">
                                    <div className="ciqt-empty-icon">📋</div>
                                    <div className="ciqt-empty-title">No Encounter Summary</div>
                                    <div className="ciqt-empty-desc">Summary not available for this encounter</div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "handwritten" && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
                            {hwLoading ? (
                                <div className="ciqt-visit-loading">
                                    <div className="ciqt-spinner" />
                                    <span>Loading handwritten note…</span>
                                </div>
                            ) : hwError ? (
                                <div className="ciqt-empty">Failed to load handwritten note</div>
                            ) : hwPages && hwPages.length > 0 ? (
                                <div style={{ padding: "8px 0", display: "flex", flexDirection: "column", height: "100%" }}>
                                    {hwDocDesc && (
                                        <div style={{
                                            fontSize: 12, fontWeight: 600, color: "#64748b",
                                            marginBottom: 8, paddingBottom: 6, width: "100%",
                                            borderBottom: "1px solid #e2e8f0",
                                        }}>
                                            {hwDocDesc} · {hwPages.length} page{hwPages.length !== 1 ? "s" : ""}
                                        </div>
                                    )}
                                    {/* Side-by-side thumbnails that fit the modal */}
                                    <div style={{
                                        display: "flex", gap: 16, justifyContent: "center", alignItems: "center",
                                        flex: 1, minHeight: 0, padding: "8px 0",
                                    }}>
                                        {hwPages.map((page, idx) => {
                                            // only media-type characters: ct ends up inside an HTML attribute (openFull) (review 10-07n finding 3)
                                            const ct = String(page.content_type || "image/png").toLowerCase().replace(/[^a-z0-9.+/;=-]/g, "") || "image/png";
                                            const isPdf = ct.includes("pdf");
                                            const src = page.data ? (isPdf ? null : `data:${ct};base64,${page.data}`) : "";
                                            let blobUrl = null;
                                            if (isPdf && page.data) {
                                                try {
                                                    const byteString = atob(page.data);
                                                    const arr = new Uint8Array(byteString.length);
                                                    for (let i = 0; i < byteString.length; i++) arr[i] = byteString.charCodeAt(i);
                                                    // typed application/pdf whatever ct says: a blob: URL runs in the care origin (review 10-07n finding 3)
                                                    const blob = new Blob([arr], { type: "application/pdf" });
                                                    blobUrl = URL.createObjectURL(blob);
                                                } catch (e) { /* ignore */ }
                                            }
                                            // For full-size open
                                            const openFull = () => {
                                                if (blobUrl) { window.open(blobUrl, '_blank'); return; }
                                                if (src) {
                                                    const w = window.open('', '_blank');
                                                    w.document.write(`<html><body style="margin:0;background:#111;display:flex;justify-content:center;align-items:center;min-height:100vh"><img src="${src}" style="max-width:100%;max-height:100vh;object-fit:contain"/></body></html>`);
                                                }
                                            };
                                            return (
                                                <div key={idx} style={{
                                                    flex: `0 1 ${hwPages.length === 1 ? '60%' : '45%'}`,
                                                    maxHeight: '100%', display: 'flex', flexDirection: 'column',
                                                    alignItems: 'center', cursor: 'pointer',
                                                }} onClick={openFull} title="Click to view full size">
                                                    <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>Page {idx + 1}</div>
                                                    {isPdf && blobUrl ? (
                                                        <iframe
                                                            src={blobUrl}
                                                            title={`Page ${idx + 1}`}
                                                            style={{
                                                                width: "100%", flex: 1, maxHeight: "calc(100% - 20px)",
                                                                border: "1px solid #e2e8f0", borderRadius: 8,
                                                                pointerEvents: "none",
                                                            }}
                                                        />
                                                    ) : (
                                                        <img
                                                            src={src}
                                                            alt={`Page ${idx + 1}`}
                                                            style={{
                                                                maxWidth: "100%", maxHeight: "calc(100% - 20px)",
                                                                objectFit: "contain", borderRadius: 8,
                                                                border: "1px solid #e2e8f0",
                                                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                                                transition: "transform 0.2s, box-shadow 0.2s",
                                                            }}
                                                            onMouseEnter={e => { e.target.style.transform = "scale(1.02)"; e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; }}
                                                            onMouseLeave={e => { e.target.style.transform = "scale(1)"; e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "center", marginTop: 4 }}>
                                        Click any page to view full size
                                    </div>
                                </div>
                            ) : (
                                <div className="ciqt-empty-state">
                                    <div className="ciqt-empty-icon">✍️</div>
                                    <div className="ciqt-empty-title">No Handwritten Note</div>
                                    <div className="ciqt-empty-desc">No progress note found for this visit date</div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "orders" && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
                            {ohLoading ? (
                                <div className="ciqt-visit-loading">
                                    <div className="ciqt-spinner" />
                                    <span>Loading order history…</span>
                                </div>
                            ) : ohError ? (
                                <div className="ciqt-empty">Failed to load order history</div>
                            ) : ohOrders && ohOrders.length > 0 ? (
                                <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
                                    <div style={{ width: "100%", maxWidth: 550 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #e2e8f0", textAlign: "center" }}>
                                            {ohOrders.length} order{ohOrders.length !== 1 ? "s" : ""} · {visitDate ? dayjs(visitDate).format("MMM D, YYYY") : ""}
                                        </div>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                            <thead>
                                                <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                                                    <th style={{ textAlign: "left", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11 }}>Order</th>
                                                    <th style={{ textAlign: "left", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 70 }}>Type</th>
                                                    <th style={{ textAlign: "center", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 90 }}>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ohOrders.map((o, i) => {
                                                    const sl = (o.status || "").toLowerCase();
                                                    const sc = sl.includes("complete") || sl.includes("done") ? { bg: "#DEF7EC", color: "#03543F" }
                                                        : sl.includes("cancel") || sl.includes("reject") ? { bg: "#FDE8E8", color: "#9B1C1C" }
                                                        : sl.includes("pend") || sl.includes("submit") || sl.includes("order") ? { bg: "#FEF3C7", color: "#92400E" }
                                                        : { bg: "#F1F5F9", color: "#475569" };
                                                    return (
                                                        <tr key={o.id || i} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fafbfc" : "#fff" }}>
                                                            <td style={{ padding: "6px 8px" }}>
                                                                <div style={{ fontWeight: 600, color: "#1e293b" }}>{o.ordername || "—"}</div>
                                                                {o.diagnosis && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>{o.diagnosis}</div>}
                                                            </td>
                                                            <td style={{ padding: "6px 8px", fontSize: 11, color: "#64748b" }}>{o.ordertype || "—"}</td>
                                                            <td style={{ padding: "6px 8px", textAlign: "center" }}>
                                                                <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: sc.bg, color: sc.color }}>{o.status || "—"}</span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        {/* Claims Data section */}
                                        {ohClaims && ohClaims.length > 0 && (
                                            <div style={{ marginTop: 20 }}>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #e2e8f0", textAlign: "center" }}>
                                                    💰 Billing · {ohClaims.length} line{ohClaims.length !== 1 ? "s" : ""}
                                                </div>
                                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                                                            <th style={{ textAlign: "left", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 55 }}>CPT</th>
                                                            <th style={{ textAlign: "left", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11 }}>Description</th>
                                                            <th style={{ textAlign: "center", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 40 }}>Qty</th>
                                                            <th style={{ textAlign: "right", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 70 }}>Charge</th>
                                                            <th style={{ textAlign: "right", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 70 }}>Payment</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {ohClaims.map((c, i) => (
                                                            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fafbfc" : "#fff" }}>
                                                                <td style={{ padding: "5px 8px", fontWeight: 600, color: "#1e293b", fontFamily: "monospace", fontSize: 11 }}>{c.cpt || "—"}</td>
                                                                <td style={{ padding: "5px 8px", color: "#334155" }}>{c.description || "—"}</td>
                                                                <td style={{ padding: "5px 8px", textAlign: "center", color: "#64748b" }}>{c.units || 1}</td>
                                                                <td style={{ padding: "5px 8px", textAlign: "right", color: "#1e293b" }}>${(c.charge || 0).toFixed(2)}</td>
                                                                <td style={{ padding: "5px 8px", textAlign: "right", color: c.payment > 0 ? "#03543F" : "#64748b" }}>${(c.payment || 0).toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="ciqt-empty-state">
                                    <div className="ciqt-empty-icon">📦</div>
                                    <div className="ciqt-empty-title">No Orders</div>
                                    <div className="ciqt-empty-desc">No orders found for this visit date</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ═══ Labs Mini-Table Modal (grouped by date) ═══ */
const LabsMiniModal = ({ labGroup, onClose, onSwitchToLabs }) => {
    /* Flatten tests + extract values, then group by panel */
    const { panels, abnormalCount, totalCount } = useMemo(() => {
        const panelMap = {};
        let abnCount = 0;
        let total = 0;
        (labGroup?.tests || []).forEach(t => {
            const lr = t.latestResult || {};
            const val = lr.value ?? lr.result ?? lr.resultvalue ?? t.value ?? '';
            if (val === '') return;
            const ref = t.refrange || lr.refrange || '';
            const oob = isOutOfRange(val, ref);
            if (oob) abnCount++;
            total++;
            const pn = t.panelname || 'Other';
            if (!panelMap[pn]) panelMap[pn] = { name: pn, tests: [], hasAbnormal: false };
            panelMap[pn].tests.push({
                name: t.testname || t.name || '—',
                value: val,
                units: lr.units || lr.unit || '',
                refrange: ref,
                abnormal: oob,
            });
            if (oob) panelMap[pn].hasAbnormal = true;
        });
        // Sort: panels with abnormals first
        const sorted = Object.values(panelMap).sort((a, b) => (b.hasAbnormal ? 1 : 0) - (a.hasAbnormal ? 1 : 0));
        return { panels: sorted, abnormalCount: abnCount, totalCount: total };
    }, [labGroup]);

    const s = {
        overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
        modal: { background: '#faf8f5', borderRadius: 16, width: '92%', maxWidth: 600, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' },
        header: { padding: '20px 24px 16px', borderBottom: '1px solid #e4ded5', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
        title: { fontSize: 18, fontWeight: 700, color: '#1a1613' },
        date: { fontSize: 12, color: '#948b7f', marginTop: 2 },
        stats: { display: 'flex', gap: 8, alignItems: 'center' },
        pill: { fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: '#f0ede8', color: '#5c5549' },
        pillAbn: { fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: '#FFF0F0', color: '#C53030' },
        closeBtn: { background: 'none', border: 'none', fontSize: 18, color: '#948b7f', cursor: 'pointer', padding: 4 },
        body: { flex: 1, overflow: 'auto', padding: '16px 20px 24px' },
        card: { background: '#fff', border: '1px solid #e4ded5', borderRadius: 12, marginBottom: 12, overflow: 'hidden' },
        cardAbn: { background: '#fff', border: '1px solid #f0bcbc', borderRadius: 12, marginBottom: 12, overflow: 'hidden' },
        cardHead: { padding: '10px 14px', borderBottom: '1px solid #f0ede8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        cardTitle: { fontSize: 13, fontWeight: 700, color: '#1a1613', textTransform: 'capitalize' },
        cardBadge: { fontSize: 9, fontWeight: 700, color: '#C53030', background: '#FFF0F0', padding: '2px 8px', borderRadius: 10 },
        cardBody: { maxHeight: 200, overflow: 'auto' },
        row: { display: 'flex', alignItems: 'center', padding: '6px 14px', borderBottom: '1px solid #f5f3ef' },
        rowAbn: { display: 'flex', alignItems: 'center', padding: '6px 14px', borderBottom: '1px solid #f5f3ef', background: '#FFF8F6' },
        tName: { flex: 1, fontSize: 12, color: '#3a342c', minWidth: 0 },
        tVal: { fontSize: 13, fontWeight: 700, color: '#1a1613', marginRight: 4 },
        tValAbn: { fontSize: 13, fontWeight: 700, color: '#C53030', marginRight: 4 },
        tUnits: { fontSize: 10, color: '#948b7f', marginRight: 10 },
        tRef: { fontSize: 10, color: '#b0a89c', fontFamily: 'monospace' },
        tFlag: { fontSize: 9, fontWeight: 700, color: '#C53030', marginLeft: 6 },
        footer: { padding: '12px 24px', borderTop: '1px solid #e4ded5', textAlign: 'center' },
        fullBtn: { background: 'none', border: '1px solid #e4ded5', borderRadius: 8, padding: '8px 20px', fontSize: 12, fontWeight: 600, color: '#e8653a', cursor: 'pointer' },
    };

    return (
        <div style={s.overlay} onClick={onClose}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
                <div style={s.header}>
                    <div>
                        <div style={s.title}>Lab Results</div>
                        <div style={s.date}>{labGroup?.dateStr || '—'}</div>
                    </div>
                    <div style={s.stats}>
                        <span style={s.pill}>{totalCount} tests</span>
                        {abnormalCount > 0 && <span style={s.pillAbn}>{abnormalCount} abnormal</span>}
                        <button style={s.closeBtn} onClick={onClose}>✕</button>
                    </div>
                </div>
                <div style={s.body}>
                    {panels.length > 0 ? panels.map((panel, pi) => {
                        const abnTests = panel.tests.filter(t => t.abnormal);
                        return (
                            <div key={pi} style={panel.hasAbnormal ? s.cardAbn : s.card}>
                                <div style={s.cardHead}>
                                    <span style={s.cardTitle}>{panel.name}</span>
                                    {abnTests.length > 0 && (
                                        <span style={s.cardBadge}>{abnTests.length} abnormal</span>
                                    )}
                                </div>
                                <div style={s.cardBody}>
                                    {panel.tests.map((t, ti) => (
                                        <div key={ti} style={t.abnormal ? s.rowAbn : s.row}>
                                            <span style={s.tName}>{t.name}</span>
                                            <span style={t.abnormal ? s.tValAbn : s.tVal}>{t.value}</span>
                                            {t.units && <span style={s.tUnits}>{t.units}</span>}
                                            {t.refrange && <span style={s.tRef}>{t.refrange}</span>}
                                            {t.abnormal && <span style={s.tFlag}>⚠</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }) : (
                        <div style={{ textAlign: 'center', padding: 40, color: '#948b7f', fontSize: 13, fontStyle: 'italic' }}>
                            No lab results available for this date
                        </div>
                    )}
                </div>
                <div style={s.footer}>
                    <button style={s.fullBtn} onClick={onSwitchToLabs}>Open Full Labs →</button>
                </div>
            </div>
        </div>
    );
};

/* ═══ Generic Detail Modal (for CareIQ items) ═══ */
const DetailModal = ({ item, onClose }) => {
    const title = item.label || item.text || item.item || item.recommendation || item.order || item.risk || item.priority_area || item.area || "Detail";
    const body = item.value || item.description || item.recommendation || item.item || item.order || item.risk || "";
    const ref = item.guideline_reference;

    return (
        <div className="ciqt-modal-bg" onClick={onClose}>
            <div className="ciqt-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ciqt-modal-head">
                    <span className="ciqt-modal-t">{title}</span>
                    <button className="ciqt-modal-x" onClick={onClose}>✕</button>
                </div>
                <div className="ciqt-modal-body">
                    {body && <p>{body}</p>}
                    {item.status && <p><strong>Status:</strong> {item.status}</p>}
                    {item.type && <p><strong>Type:</strong> {item.type}</p>}
                    {ref && (
                        <div className="ciqt-guideline-box">
                            <h4>📎 Guideline Reference</h4>
                            {ref.source && <p><strong>Source:</strong> {ref.source}</p>}
                            {ref.link && ref.link !== "NOT_FOUND" && (
                                <p><a href={ref.link} target="_blank" rel="noreferrer">{ref.link}</a></p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ═══ Vitals helpers (from Summary's Frame.jsx) ═══ */
const calculateBMI = (w, unit, ft, inch) => {
    const weightNum = parseFloat(w);
    const heightInches = parseFloat(ft || 0) * 12 + parseFloat(inch || 0);
    if (!weightNum || !heightInches) return "";
    const meters = heightInches * 0.0254;
    const kg = unit === "lbs" ? weightNum * 0.45359237 : unit === "stone" ? weightNum * 6.35029 : weightNum;
    return (kg / (meters * meters)).toFixed(1);
};

/* ─── BP severity classification ─── */
const classifyBP = (sys, dia) => {
    const s = parseInt(sys, 10);
    const d = parseInt(dia, 10);
    if (isNaN(s) || isNaN(d)) return { level: "normal", label: "" };
    if (s > 180 || d > 120) return { level: "crisis", label: "CRISIS" };
    if (s >= 140 || d >= 90) return { level: "stage2", label: "STAGE 2" };
    if (s >= 130 || d >= 80) return { level: "stage1", label: "STAGE 1" };
    if (s < 120 && d < 80) return { level: "normal", label: "NORMAL" };
    return { level: "elevated", label: "ELEVATED" };
};

/* ─── Order status styling ─── */
const orderStatusClass = (status) => {
    if (!status) return "";
    const s = status.toLowerCase();
    if (s === "completed" || s === "complete") return "ciqt-ostatus-completed";
    if (s === "pending" || s === "submitted") return "ciqt-ostatus-pending";
    if (s === "active" || s === "open") return "ciqt-ostatus-active";
    return "ciqt-ostatus-default";
};

/* ─── Severity helpers for AI insights ─── */
const severityClass = (sev) => {
    if (!sev) return "medium";
    const s = sev.toLowerCase();
    if (s === "high" || s === "urgent" || s === "critical") return "high";
    if (s === "low" || s === "minor") return "low";
    return "medium";
};

const formatVitalsToCards = (vitals) => {
    const cards = [];
    const bmiValue = calculateBMI(vitals?.weight, vitals?.weight_cel, vitals?.height_ft, vitals?.height_in);

    if (vitals.bp_mm && vitals.bp_hgg) {
        const bp = classifyBP(vitals.bp_mm, vitals.bp_hgg);
        cards.push({ title: "BP", value: `${vitals.bp_mm}/${vitals.bp_hgg}`, subValue: "mmHg", context: bp.label || vitals.bp_pos || "", bpLevel: bp.level, priority: 1 });
    }
    if (vitals.plus_bpm) cards.push({ title: "PULSE", value: `${vitals.plus_bpm}`, subValue: "bpm", context: vitals.plus_drop || "", priority: 2 });
    if (vitals.height_ft || vitals.height_in) cards.push({ title: "HEIGHT", value: `${vitals.height_ft || ""}'${vitals.height_in || ""}"`, subValue: "", context: vitals.height_pos || "", priority: 3 });
    if (vitals.weight) cards.push({ title: "WEIGHT", value: `${vitals.weight}`, subValue: `${vitals.weight_cel || ""}`, context: vitals.weight_drop || "", priority: 4 });
    if (vitals.temp) cards.push({ title: "TEMP", value: `${vitals.temp}`, subValue: `${vitals.temp_cel || "°F"}`, context: vitals.temp_drop || "", priority: 5 });
    if (vitals.o2_1 || vitals.o2_2) cards.push({ title: "O₂ SAT", value: `${vitals.o2_1 || vitals.o2_2}`, subValue: "%", context: vitals.o2_drop || "", priority: 6 });
    if (vitals.pain_level) cards.push({ title: "PAIN", value: `${vitals.pain_level}`, subValue: "/10", context: "", priority: 8 });
    if (bmiValue && !isNaN(bmiValue)) cards.push({ title: "BMI", value: bmiValue, context: "", priority: 7 });
    if (vitals.respiratory) cards.push({ title: "RESP", value: `${vitals.respiratory}`, subValue: "bpm", context: vitals.respiratory_drop || "", priority: 9 });
    if (vitals.bg) cards.push({ title: "GLUCOSE", value: `${vitals.bg}`, subValue: `${vitals.bg_cel || "mg/dl"}`, context: vitals.bg_drop || "", priority: 10 });

    return cards.sort((a, b) => a.priority - b.priority);
};

/* ═══ Orders grouping helper (from Summary) ═══ */
const groupOrdersByType = (orders) => {
    const grouped = {};
    orders.forEach((order) => {
        const type = order.ordertype || "Other";
        if (!grouped[type]) grouped[type] = [];
        grouped[type].push(order);
    });
    return grouped;
};

const orderTypeIcon = (type) => {
    const t = (type || "").toLowerCase();
    if (t === "labs") return "🧪";
    if (t === "visit" || t === "visits") return "🏥";
    if (t === "injections") return "💉";
    if (t === "refills") return "💊";
    if (t === "referrals") return "📋";
    if (t === "notes") return "📝";
    if (t === "schedule-rto") return "📅";
    return "📄";
};

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
// Provider names often arrive in capitals ("OCO MONTOYA, NP"); show them in
// name case, keeping credentials (NP, MD, ...) upper case.
const CREDENTIALS = new Set(["MD", "DO", "NP", "PA", "PAC", "PA-C", "FNP", "APRN", "RN", "LVN", "MA", "DNP", "PHD", "DPM", "OD", "DC"]);
const providerDisplayName = (name) => {
    const s = String(name || "").trim();
    if (!s || s !== s.toUpperCase()) return s;
    return s.toLowerCase().replace(/[a-z][a-z'.-]*/g, (w) =>
        CREDENTIALS.has(w.toUpperCase().replace(/\./g, "")) ? w.toUpperCase()
            : w.replace(/(^|[-'])([a-z])/g, (_, sep, c) => sep + c.toUpperCase()));
};

export default function CareIQTab({ dataId, onSwitchTab, setPendingResults: onPendingResults, showTimeline = true, onCloseTimeline, onToggleTimeline }) {
    const patientId = dataId?.patientid;
    const chartOrderDate = useMemo(() => {
        const rawDate = dataId?.startdate || dataId?.encounterdate || dataId?.date;
        const parsed = dayjs(rawDate);
        return parsed.isValid() ? parsed.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
    }, [dataId]);
    const chartProviderName = dataId?.linked_provider || dataId?.providername || "";
    const chartEncounterId = Number(dataId?.encounterid) || 0;

    const [careIQ, setCareIQ] = useState(null);
    const [encounters, setEncounters] = useState([]);
    // All BlinkScribe-documented dates for this patient — visits recorded on a
    // local/legacy appointment (e.g. migrated 1111/Dallas history) have no
    // Athena encounter, so the FHIR timeline would silently omit them
    // (QA 2026-08-19, pt 13795 Whitney Wade Aug-7 visit).
    const [bsVisits, setBsVisits] = useState([]);
    const [labPanels, setLabPanels] = useState([]);
    // Imaging + clinical lists, kept per category so a list the server is still
    // refreshing can be swapped in on its own; `documents` is the merged view.
    const [docsByCat, setDocsByCat] = useState(NO_BASE_DOCS);
    const documents = useMemo(
        () => [...docsByCat.imagingresult, ...docsByCat.clinicaldocument], [docsByCat]);
    const [docsLoading, setDocsLoading] = useState(true);
    // {category: {asOf, refreshing, polling}} from the list's meta (null = none)
    const [docsMeta, setDocsMeta] = useState({});
    const [handwrittenDocs, setHandwrittenDocs] = useState([]); // progress notes used to flag visits w/ a handwritten note
    const [docLabels, setDocLabels] = useState({});
    const [loading, setLoading] = useState(true);
    const [allLabsLoaded, setAllLabsLoaded] = useState(false);
    const [allLabsLoading, setAllLabsLoading] = useState(false);
    const [timelineLoading, setTimelineLoading] = useState(true);

    // Vitals state
    const [vitals, setVitals] = useState([]);
    const [vitalsLoading, setVitalsLoading] = useState(false);
    const [showAllVitals, setShowAllVitals] = useState(false);

    // Orders state
    const [currentOrders, setCurrentOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const reduxOrdersData = useSelector((s) => s.ordersData?.ordersdata);

    // AI Insights tab state
    const [insightsTab, setInsightsTab] = useState("gaps");
    const [tlFilter, setTlFilter] = useState("all");       // timeline type filter
    const [tlTypeOpen, setTlTypeOpen] = useState(false);   // type dropdown
    const tlTypeRef = useRef(null);
    useEffect(() => {
        if (!tlTypeOpen) return undefined;
        const onDown = (e) => { if (tlTypeRef.current && !tlTypeRef.current.contains(e.target)) setTlTypeOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setTlTypeOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
    }, [tlTypeOpen]);
    const [cvExpanded, setCvExpanded] = useState(false);   // critical values: all labs

    // Force rerender on orientation change so iOS Safari re-evaluates zoom media queries
    const [, setOrientationKey] = useState(0);
    useEffect(() => {
        const onResize = () => setOrientationKey(k => k + 1);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // Modal states
    const [modalItem, setModalItem] = useState(null);
    const [visitModal, setVisitModal] = useState(null);
    const [labModal, setLabModal] = useState(null);
    const [docModal, setDocModal] = useState(null);
    const [docModalOpen, setDocModalOpen] = useState(false);

    const [timelineSearch, setTimelineSearch] = useState("");

    // ── LiveScribe diagnosis integration ──
    // suggestedOrders / suggestedICD10 from RecordingContext are React Context
    // state — they persist across patient navigation until clearLastNote()
    // fires (which happens async via sideframe.jsx on patient change). To
    // prevent Patient A's just-recorded suggestions from flashing onto
    // Patient B's chart during the gap, we gate consumption by patient-id
    // match — only consume when the live-state is for THIS patient.
    const {
        isGlobalRecording,
        suggestedOrders: rawLiveScribeOrders,
        suggestedICD10: rawLiveScribeDx,
        patientInfo: recordingPatientInfo,
        lastNotePatientId,
    } = useRecording();
    const liveStateBelongsToThisPatient =
        !!patientId && (
            String(recordingPatientInfo?.patientid || "") === String(patientId) ||
            String(lastNotePatientId || "") === String(patientId)
        );
    const liveScribeOrders = liveStateBelongsToThisPatient ? rawLiveScribeOrders : [];
    const liveScribeDx = liveStateBelongsToThisPatient ? rawLiveScribeDx : [];
    const reduxDiagnoses = useSelector((state) => state.orders?.orders?.diagnosis || []);
    const [aiDiagnoses, setAiDiagnoses] = useState([]);
    const [addedOrders, setAddedOrders] = useState(new Set());

    // Persist confirmedDx (and addedOrders) per encounter so they survive
    // tab navigation. Without this, switching to CCM and back wipes the
    // local state and Current DX panel goes empty (Redux doesn't get the
    // POSTed diagnoses, so seeding from reduxDiagnoses fails).
    //
    // persistKey is null until appointmentid is known. Otherwise the early
    // renders fall back to a "noappt" key, the user confirms a Dx → it's
    // saved to noappt, then appointmentid resolves and the new key load
    // races with the save: the save effect runs first (closure has the
    // pre-load empty Set) and overwrites the just-loaded data with empty.
    // Skipping read/write when key is null avoids both halves of that bug.
    const persistKey = useMemo(() => {
        const aid = dataId?.appointmentid;
        if (!patientId || !aid) return null;
        return `careiq:confirmedDx:${patientId}:${aid}`;
    }, [patientId, dataId?.appointmentid]);
    const orderPersistKey = useMemo(() => {
        const aid = dataId?.appointmentid;
        if (!patientId || !aid) return null;
        return `careiq:addedOrders:${patientId}:${aid}`;
    }, [patientId, dataId?.appointmentid]);
    // aiDiagnoses needs its own persisted key — after the post-recording job
    // finalizes, note_json no longer carries `suggested_icd10` and
    // RecordingContext.clearLastNote() empties liveScribeDx. Without persisting
    // the full {icd10_code, description} list, the Current DX pill row falls
    // back to nothing on tab re-mount because it filters aiDiagnoses by
    // confirmedDx — confirmedDx alone is just keys, not enough to render.
    const aiDiagnosesPersistKey = useMemo(() => {
        const aid = dataId?.appointmentid;
        if (!patientId || !aid) return null;
        return `careiq:aiDiagnoses:${patientId}:${aid}`;
    }, [patientId, dataId?.appointmentid]);

    const [confirmedDx, setConfirmedDx] = useState(new Set());

    // Tracks which key we've most recently loaded for. Used by the save
    // effect to skip the very first invocation after a key change — that
    // invocation has stale closure state and would otherwise overwrite
    // the data the load effect just put in place.
    const loadedKeyRef = useRef(null);
    const loadedOrderKeyRef = useRef(null);
    const loadedAiDxKeyRef = useRef(null);

    // Load from localStorage when key changes (mount, patient switch,
    // or appointmentid resolving from undefined → real value).
    useEffect(() => {
        if (!persistKey) return;
        try {
            const raw = localStorage.getItem(persistKey);
            const arr = raw ? JSON.parse(raw) : [];
            setConfirmedDx(new Set(Array.isArray(arr) ? arr : []));
        } catch (_) { setConfirmedDx(new Set()); }
        loadedKeyRef.current = persistKey;
    }, [persistKey]);

    useEffect(() => {
        if (!orderPersistKey) return;
        try {
            const raw = localStorage.getItem(orderPersistKey);
            const arr = raw ? JSON.parse(raw) : [];
            setAddedOrders(new Set(Array.isArray(arr) ? arr : []));
        } catch (_) { setAddedOrders(new Set()); }
        loadedOrderKeyRef.current = orderPersistKey;
    }, [orderPersistKey]);

    // Save confirmedDx to localStorage whenever it changes — but skip
    // the run that fires immediately after a key change, since that
    // run's closure still holds the pre-load state.
    useEffect(() => {
        if (!persistKey) return;
        if (loadedKeyRef.current !== persistKey) return;
        try {
            localStorage.setItem(persistKey, JSON.stringify(Array.from(confirmedDx)));
        } catch (_) { /* localStorage full / disabled — non-fatal */ }
    }, [confirmedDx, persistKey]);

    useEffect(() => {
        if (!orderPersistKey) return;
        if (loadedOrderKeyRef.current !== orderPersistKey) return;
        try {
            localStorage.setItem(orderPersistKey, JSON.stringify(Array.from(addedOrders)));
        } catch (_) { /* non-fatal */ }
    }, [addedOrders, orderPersistKey]);

    // Load + save aiDiagnoses (full {icd10_code, description} objects) so the
    // Current DX pill row keeps rendering after tab re-mount, even when the
    // post-recording finalize has stripped suggested_icd10 from note_json.
    useEffect(() => {
        if (!aiDiagnosesPersistKey) return;
        try {
            const raw = localStorage.getItem(aiDiagnosesPersistKey);
            const arr = raw ? JSON.parse(raw) : [];
            if (Array.isArray(arr) && arr.length > 0) setAiDiagnoses(arr);
        } catch (_) { /* non-fatal */ }
        loadedAiDxKeyRef.current = aiDiagnosesPersistKey;
    }, [aiDiagnosesPersistKey]);

    useEffect(() => {
        if (!aiDiagnosesPersistKey) return;
        if (loadedAiDxKeyRef.current !== aiDiagnosesPersistKey) return;
        try {
            localStorage.setItem(aiDiagnosesPersistKey, JSON.stringify(aiDiagnoses));
        } catch (_) { /* non-fatal */ }
    }, [aiDiagnoses, aiDiagnosesPersistKey]);

    // Seed confirmedDx from existing confirmed diagnoses (from orders/Redux)
    // so that already-confirmed Dx show "✓" instead of "+" after re-mount
    useEffect(() => {
        if (reduxDiagnoses.length > 0) {
            setConfirmedDx((prev) => {
                const next = new Set(prev);
                reduxDiagnoses.forEach((c) => {
                    if (c.condition_name) next.add(c.condition_name);
                    if (c.icd10) next.add(c.icd10);
                });
                return next;
            });
        }
    }, [reduxDiagnoses]);

    // Seed aiDiagnoses from RecordingContext on mount/tab-switch so suggested
    // diagnoses survive navigating away and back to CareIQ. RecordingContext
    // stores them as formatted strings ("ICD - description") via setSuggestedICD10
    // — parse back into {icd10_code, description} shape used by this component.
    useEffect(() => {
        if (!Array.isArray(liveScribeDx) || liveScribeDx.length === 0) return;
        setAiDiagnoses((prev) => {
            const seenDesc = new Set(prev.map((d) => (d.description || "").trim()));
            const next = [...prev];
            for (const entry of liveScribeDx) {
                if (typeof entry !== "string") continue;
                // Format produced by RecordingContext: "ICD-CODE - description"
                const m = entry.match(/^([A-Z][0-9A-Z.]+)\s*-\s*(.+)$/);
                const obj = m
                    ? { icd10_code: m[1].trim(), description: m[2].trim() }
                    : { icd10_code: "", description: entry.trim() };
                if (!seenDesc.has(obj.description)) {
                    seenDesc.add(obj.description);
                    next.push(obj);
                }
            }
            return next;
        });
    }, [liveScribeDx]);

    // Listen for LiveScribe suggested diagnoses (live, in-session events)
    useEffect(() => {
        const handleDx = (data) => {
            setAiDiagnoses((prev) => {
                const exists = prev.some((d) => d.description === data.description);
                if (exists) return prev;
                return [...prev, { icd10_code: data.icd10_code, description: data.description }];
            });
        };
        eventBus.on("liveScribeDiagnosis", handleDx);
        return () => eventBus.off("liveScribeDiagnosis", handleDx);
    }, []);

    /* ── Re-fetch orders when Orders tab saves/deletes (emits "fetchSummary") ── */
    useEffect(() => {
        const handleOrdersChanged = async () => {
            if (!patientId) return;
            try {
                const raw = await careiqapi.post(`/v1/${practiceId}/orders/get`, {
                    patientid: patientId,
                    orderdate: chartOrderDate,
                });
                const d = raw?.data || raw;
                const orders = Array.isArray(d) ? d : (d?.orders || d?.data || []);
                setCurrentOrders(orders);
            } catch (e) {
                console.warn("[CareIQ] orders refetch on fetchSummary failed:", e?.message);
            }
        };
        eventBus.on("fetchSummary", handleOrdersChanged);
        return () => eventBus.off("fetchSummary", handleOrdersChanged);
    }, [patientId, practiceId, chartOrderDate]);

    // ── Persist Dx: fetch today's recording and seed aiDiagnoses on mount ──
    useEffect(() => {
        if (!patientId || isGlobalRecording) return;
        let cancelled = false;
        const fetchDx = async () => {
            try {
                const today = new Date().toISOString().slice(0, 10);
                const res = await blinkscribeapi.post(
                    `/v1/patient/${patientId}/recording/details`,
                    { filter_array: { date_filter: { start_date: today, end_date: today } } }
                );
                if (cancelled) return;
                const records = res?.data || res;
                if (Array.isArray(records) && records.length > 0) {
                    const latest = records.sort((a, b) => new Date(b.ts) - new Date(a.ts))[0];
                    const nj = latest?.note_json;
                    // Live scribe checkpoint: suggested_icd10 at top level
                    // Final note: no separate array, but may have ICD-10 in assessment_plan
                    const savedDx = nj?.suggested_icd10 || [];
                    if (savedDx.length > 0) {
                        setAiDiagnoses((prev) => {
                            const existing = new Set(prev.map((d) => d.description));
                            const newDx = savedDx
                                .filter((d) => d.description && !existing.has(d.description))
                                .map((d) => ({ icd10_code: d.icd10_code || d.icd10 || "", description: d.description }));
                            return newDx.length ? [...prev, ...newDx] : prev;
                        });
                    }
                }
            } catch (e) {
                console.warn("[CareIQTab] Failed to fetch persisted Dx:", e?.message);
            }
        };
        fetchDx();
        return () => { cancelled = true; };
    }, [patientId, isGlobalRecording]);



    const [timelineExpanded, setTimelineExpanded] = useState(false);

    /* ── chart preferences: practice-level (registry) + per-user timeline override ── */
    const dispatch = useDispatch();
    const userConfig = useSelector((state) => state.userconfig);
    /* Level of Visit for the VISIT order row: the sidebar's live-edited
       value first (same redux slot sideframe writes), else the appointment
       row. When a note fills the sidebar the LOV selector is below the
       fold, so Current Orders is where the doctor reads it (QA 2026-08-24). */
    const reduxLov = useSelector((state) => state.dump?.storedvalues?.find?.((i) => i.id === "lov")?.value);
    const [noteLov, setNoteLov] = useState(0);
    useEffect(() => {
        const onNoteLov = (n) => setNoteLov(Number(n) || 0);
        eventBus.on("noteLovLoaded", onNoteLov);
        return () => eventBus.off("noteLovLoaded", onNoteLov);
    }, []);
    const visitLov = Number(reduxLov) || noteLov || Number(dataId?.lov) || 0;
    // New-patient visits render the 99202-05 family (level-1 clamps to
    // 99202 — 99201 was deleted 2021); everything else 99211-15. Same
    // conditional as the sidebar chips — the stored level stays 1-5.
    const reduxTov = useSelector((state) => state.dump?.storedvalues?.find?.((i) => i.id === "tov")?.value);
    // Athena appointment notes = the "reason" line athenaNet shows under the
    // appointment type (e.g. "Type 2 Diabetes", lab-prep instructions). Not
    // synced into appointment_master, so fetched via the mirror passthrough;
    // empty for most appointments (user request 2026-09-02).
    const [apptReason, setApptReason] = useState("");
    useEffect(() => {
        const apptId = dataId?.appointmentid;
        setApptReason("");
        if (!apptId) return;
        let cancelled = false;
        careiqapi.get(`/v1/fhir-mirror/${practiceId}/appointments/${apptId}/notes`)
            .then((res) => {
                if (cancelled) return;
                const d = res?.data || res;
                const notes = Array.isArray(d?.notes) ? d.notes : [];
                setApptReason(notes.map((n) => (n.notetext || "").trim()).filter(Boolean).join(" · "));
            })
            .catch(() => { /* nicety only — never surface an error for it */ });
        return () => { cancelled = true; };
    }, [dataId?.appointmentid]);

    const visitTov = reduxTov || dataId?.tov;
    const visitIsNewPatient = visitTov
        ? visitTov === "New Patient"
        : /\bnew\b/i.test(String(dataId?.purpose || ""));
    const visitLovCode = visitLov >= 1 && visitLov <= 5
        ? String(visitIsNewPatient ? 99200 + Math.max(2, visitLov) : 99210 + visitLov)
        : null;
    const [practicePrefs, setPracticePrefs] = useState(null);   // {timeline:{docs},careiq:{sections}} or null
    const [tlPrefsOpen, setTlPrefsOpen] = useState(false);
    const [tlPrefsSaving, setTlPrefsSaving] = useState(false);
    const [tlDraft, setTlDraft] = useState(DEFAULT_TIMELINE_DOCS);
    const [encDocuments, setEncDocuments] = useState([]);       // fetched only when opted in
    const [encDocsMeta, setEncDocsMeta] = useState(null);
    const [encDocsLoading, setEncDocsLoading] = useState(false);

    const timelineDocs = useMemo(() => {
        const user = userConfig?.config?.timeline_docs;
        if (Array.isArray(user) && user.length > 0) return user;
        const practice = practicePrefs?.timeline?.docs;
        if (Array.isArray(practice) && practice.length > 0) return practice;
        return DEFAULT_TIMELINE_DOCS;
    }, [userConfig, practicePrefs]);

    const ciqSections = practicePrefs?.careiq?.sections || null;
    // no config (or key absent) = section visible — matches pre-preferences behavior
    const sectionOn = useCallback((k) => !ciqSections || ciqSections[k] !== false, [ciqSections]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await careiqapi.get(`/v1/${practiceId}/chart-preferences`);
                const body = r?.data ?? r;
                const prefs = body?.data ?? body;
                if (!cancelled && prefs && (prefs.timeline !== undefined || prefs.careiq !== undefined)) {
                    setPracticePrefs(prefs);
                }
            } catch (e) {
                console.warn("[CareIQ] chart-preferences fetch failed (using defaults):", e?.message);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // encounter documents are NOT part of the base fetch (deliberate — noise +
    // extra FHIR calls); pull them only when the effective preference wants them
    const wantsEncDocs = timelineDocs.includes("encounterdocument");
    useEffect(() => {
        // The timeline paints before this list arrives (a rebuild can take ~40 s), and the tab
        // is not keyed by patient: drop the previous patient's rows before fetching.
        setEncDocuments([]);
        setEncDocsMeta(null);
        if (!patientId || !wantsEncDocs) { setEncDocsLoading(false); return; }
        let cancelled = false;
        let stopRereads = null;
        const isCancelled = () => cancelled;
        const url = docListUrl(practiceId, patientId, "encounterdocument");   // allow_stale=1: CareIQ tab only
        setEncDocsLoading(true);
        (async () => {
            try {
                const r = await withRetry(() => careiqapi.get(url), { isCancelled });
                if (cancelled) return;
                stopRereads = applyListAnswer({
                    body: r, wrapKey: "encounterdocuments", reread: () => careiqapi.get(url), isCancelled,
                    onList: setEncDocuments, onMeta: setEncDocsMeta,
                });
            } catch (e) {
                console.warn("[CareIQ] encounterdocument fetch failed:", e?.message);
            } finally {
                if (!cancelled) setEncDocsLoading(false);
            }
        })();
        return () => { cancelled = true; if (stopRereads) stopRereads(); };
    }, [patientId, wantsEncDocs]);

    // keep the AI-insights tab on a visible section when practice config hides some
    useEffect(() => {
        const keyOf = { gaps: "care_gaps", recs: "recommendations", risks: "risk_factors" };
        if (!sectionOn(keyOf[insightsTab])) {
            const first = Object.entries(keyOf).find(([, key]) => sectionOn(key));
            if (first) setInsightsTab(first[0]);
        }
    }, [sectionOn, insightsTab]);

    const saveTimelinePrefs = useCallback(async () => {
        if (!userConfig?.id) { setTlPrefsOpen(false); return; }
        setTlPrefsSaving(true);
        try {
            await api.put(`/v1/${practiceId}/user/config`, { rowid: userConfig.id, timeline_docs: tlDraft });
            dispatch(setConfig({ ...userConfig, config: { ...userConfig.config, timeline_docs: tlDraft } }));
            setTlPrefsOpen(false);
        } catch (e) {
            console.error("[CareIQ] failed to save timeline preferences:", e);
        } finally {
            setTlPrefsSaving(false);
        }
    }, [userConfig, tlDraft, dispatch]);

    // Order editing state
    const [editingOrder, setEditingOrder] = useState(null); // the order object being edited
    const [editingOrderName, setEditingOrderName] = useState("");
    const [editingSaving, setEditingSaving] = useState(false);
    const [confirmDeleteOrder, setConfirmDeleteOrder] = useState(null); // order awaiting delete confirmation

    /* ── dynamic sidebar height ── */
    const sidebarRef = useRef(null);
    const [sidebarH, setSidebarH] = useState(null);
    useEffect(() => {
        const calc = () => {
            if (!sidebarRef.current) return;
            const rect = sidebarRef.current.getBoundingClientRect();
            const h = window.innerHeight - rect.top - 16;       // 16px bottom padding
            setSidebarH(h > 200 ? h + "px" : "200px");
        };
        calc();
        window.addEventListener("resize", calc);
        const timer = setTimeout(calc, 500);                     // recalc after layout settles
        return () => { window.removeEventListener("resize", calc); clearTimeout(timer); };
    }, [loading]);

    /* ── fetch: the tab paints once todo, labs, vitals and orders are in ──
       The document lists can take ~40 s when the server rebuilds them (TRACE.md,
       pt 914), so they and the three timeline calls fill the timeline after the
       first paint instead of holding the whole tab behind a spinner. Every read
       here retries once on 502/503/504 or no answer (a gateway restart). */
    useEffect(() => {
        if (!patientId) return;
        let cancelled = false;
        const isCancelled = () => cancelled;
        const read = (fn) => withRetry(fn, { isCancelled });
        const stopRereads = [];

        const loadDocList = ([cat, wrapKey]) => {
            const url = docListUrl(practiceId, patientId, cat);   // allow_stale=1: CareIQ tab only
            return read(() => careiqapi.get(url))
                .then((body) => {
                    if (cancelled) return;
                    const stop = applyListAnswer({
                        body, wrapKey, reread: () => careiqapi.get(url), isCancelled,
                        onList: (rows) => setDocsByCat((prev) => ({ ...prev, [cat]: rows })),
                        onMeta: (m) => setDocsMeta((prev) => ({ ...prev, [cat]: m })),
                    });
                    if (stop) stopRereads.push(stop);
                })
                .catch((e) => console.warn(`[CareIQ] ${cat} fetch failed:`, e?.message || e));
        };

        // ── Encounters (slow FHIR call on a cache miss) ──
        const loadEncounters = async () => {
            try {
                // Practice 9928 is two locations (1 = Odessa, 2 = Dallas/Frisco);
                // hardcoding 1 returned Odessa's encounters for Dallas patients.
                const encRes = await read(() => careiqapi.post(`/v1/fhir-mirror/${practiceId}/patient/${patientId}/encounter`, { departmentid: Number(dataId?.departmentid) || Number(userConfig?.config?.departmentid) || 1, limit: 5 }));
                if (!cancelled) {
                    const d = encRes?.data?.encounters || [];
                    setEncounters(Array.isArray(d) ? d : []);
                }
            } catch (e) {
                console.warn("[CareIQ] encounters fetch failed:", e?.message);
            } finally {
                if (!cancelled) setTimelineLoading(false);
            }
        };

        // ── BlinkScribe-documented dates (for visits with no Athena encounter) ──
        const loadBsVisits = async () => {
            try {
                const bsRes = await read(() => blinkscribeapi.post(
                    `/v1/patient/${patientId}/recording/details`,
                    { filter_array: { date_filter: { start_date: "2020-01-01", end_date: dayjs().format("YYYY-MM-DD") } } }
                ));
                if (!cancelled) {
                    const recs = Array.isArray(bsRes?.data) ? bsRes.data : (Array.isArray(bsRes) ? bsRes : []);
                    setBsVisits(recs.filter((r) => r &&
                        (r.transcriptionstatus === "Summarized" || r.transcriptionstatus === "APPROVED")));
                }
            } catch (e) {
                console.warn("[CareIQ] blinkscribe visit dates fetch failed:", e?.message);
            }
        };

        // ── Flag visits that have a handwritten note (doctor request, Jun
        //    2026). Cache-first FHIR-mirror call (NOT a paid Athena call);
        //    same endpoint/cache the per-visit modal uses, fetched once. ──
        const loadHandwritten = async () => {
            try {
                const hwRes = await read(() => careiqapi.post(
                    `/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/get`,
                    { category: "ProgressNotes", department_id: 1 }
                ));
                const hwData = hwRes?.data || hwRes;
                const hwDocs = hwData?.data?.encounterdocuments || hwData?.encounterdocuments || hwData?.data || [];
                const exclude = ["ekg", "ecg", "electrocardiogram", "imaging", "x-ray", "xray", "mri", "ct scan", "ultrasound", "lab"];
                const handwritten = (Array.isArray(hwDocs) ? hwDocs : []).filter((d) => {
                    const desc = (d.documentdescription || d.description || "").toLowerCase();
                    return !exclude.some((p) => desc.includes(p));
                });
                if (!cancelled) setHandwrittenDocs(handwritten);
            } catch (e) {
                console.warn("[CareIQ] progress notes fetch failed:", e?.message);
            }
        };

        const fetchAll = async () => {
            setLoading(true);
            setVitalsLoading(true);
            setOrdersLoading(true);
            setDocsLoading(true);
            setTimelineLoading(true);
            // The timeline is visible before its calls finish now, so rows from
            // the previous patient or visit must not linger in it meanwhile.
            setDocsByCat(NO_BASE_DOCS);
            setDocsMeta({});
            setEncounters([]);
            setBsVisits([]);
            setHandwrittenDocs([]);

            // The fast four go first: the browser queues requests per host, so
            // they get connections ahead of the slow document lists.
            const core = Promise.allSettled([
                // 0: CareIQ insights
                read(() => api.get(`/v1/kg/${practiceId}/todo/${patientId}`)),
                // 1: Labs (from local DB with proper panel grouping)
                read(() => api.get(`/v1/${practiceId}/labs/patient/${patientId}/get`)),
                // 2: Vitals
                read(() => careiqapi.post(`/v1/fhir-mirror/${practiceId}/vitals/patient/${patientId}/vitalscurrent`)),
                // 3: Current orders
                read(() => careiqapi.post(`/v1/${practiceId}/orders/get`, {
                    patientid: patientId,
                    orderdate: chartOrderDate,
                })),
            ]);

            // Imaging + clinical documents (discharge summaries, referrals,
            // external CCDA, etc.) — non-visit-derived docs the doctor asked to
            // see alongside imaging on the unified timeline. Each entry keeps its
            // category-specific id (imagingresultid / clinicaldocumentid) so the
            // timeline can match docLabels and the doc viewer routes the click.
            // Encounter documents (in-office visit notes split across
            // hpiNote/apNote/peNote/rosNote DRs) are intentionally NOT fetched
            // here: the visit is already on the timeline via the encounter feed.
            // Not awaited before the first paint.
            Promise.allSettled(BASE_DOC_LISTS.map(loadDocList))
                .then(() => { if (!cancelled) setDocsLoading(false); });

            const results = await core;
            if (cancelled) return;

            if (results[0].status === "fulfilled") {
                const d = results[0].value?.data || results[0].value;
                const insights = d?.patient_insights || d;
                setCareIQ(insights);
                const kgLabels = d?.document_labels || insights?.document_labels || [];
                if (Array.isArray(kgLabels) && kgLabels.length > 0) {
                    const labelMap = {};
                    kgLabels.forEach((dl) => {
                        if (dl.documentid && dl.label) labelMap[dl.documentid] = dl.label;
                    });
                    setDocLabels(labelMap);
                }
            }
            if (results[1].status === "fulfilled") {
                const d = results[1].value?.data || results[1].value;
                setLabPanels(d?.panels || []);
            }
            if (results[2].status === "fulfilled") {
                const d = results[2].value?.data || results[2].value;
                setVitals(Array.isArray(d) ? d : []);
            }
            if (results[3].status === "fulfilled") {
                const raw = results[3].value?.data || results[3].value;
                const d = Array.isArray(raw) ? raw : (raw?.orders || raw?.data || []);
                setCurrentOrders(Array.isArray(d) ? d : []);
            } else {
                console.warn("[CareIQ] orders fetch failed:", results[3].reason);
            }

            setLoading(false);
            setVitalsLoading(false);
            setOrdersLoading(false);

            // ── Timeline calls: independent of each other, so in parallel ──
            await Promise.allSettled([loadEncounters(), loadBsVisits(), loadHandwritten()]);
        };

        fetchAll();
        return () => { cancelled = true; stopRereads.forEach((stop) => stop()); };
    }, [patientId, chartOrderDate]);

    /* ── Re-fetch orders when Redux ordersData changes (user added/removed via Order module) ── */
    useEffect(() => {
        if (!patientId || loading) return;
        let cancelled = false;
        const refetchOrders = async () => {
            try {
                const raw = await careiqapi.post(`/v1/${practiceId}/orders/get`, {
                    patientid: patientId,
                    orderdate: chartOrderDate,
                });
                if (cancelled) return;
                const d = raw?.data || raw;
                const orders = Array.isArray(d) ? d : (d?.orders || d?.data || []);
                setCurrentOrders(orders);
            } catch (e) {
                console.warn("[CareIQ] orders refetch failed:", e?.message);
            }
        };
        refetchOrders();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduxOrdersData, chartOrderDate]);

    /* ── Seed addedOrders from DB-fetched orders to grey-out already-added suggestions ── */
    useEffect(() => {
        if (!currentOrders || currentOrders.length === 0) return;
        const namesFromDb = new Set(
            currentOrders.map((o) => (o.ordername || "").trim()).filter(Boolean)
        );
        setAddedOrders((prev) => {
            const merged = new Set([...prev, ...namesFromDb]);
            if (merged.size === prev.size) return prev;
            return merged;
        });
    }, [currentOrders]);

    /* ── load all labs: sync latest FHIR → DB, then read from DB ── */
    const loadAllLabs = useCallback(async () => {
        if (allLabsLoaded || allLabsLoading || !patientId) return;
        setAllLabsLoading(true);
        try {
            // 1. Sync latest FHIR data into LabMaster DB
            try { await api.get(`/v1/${practiceId}/patient/${patientId}/labresults`); } catch (_) { /* sync is best-effort */ }
            // 2. Read from DB (has real panel names like CBC, CMP)
            const resp = await api.get(`/v1/${practiceId}/labs/patient/${patientId}/get`);
            const d = resp?.data || resp;
            setLabPanels(d?.panels || []);
            setAllLabsLoaded(true);
        } catch (err) {
            console.warn('[CareIQ] full labs fetch failed:', err);
        } finally {
            setAllLabsLoading(false);
        }
    }, [patientId, allLabsLoaded, allLabsLoading]);

    /* ── derived CareIQ data ── */
    const ciq = careIQ || {};
    const criticalAlerts = useMemo(() => {
        const rows = ciq?.["critical_alerts/clinical_intel"]?.data_rows || [];
        return rows.filter((r) => ["abnormal", "high", "overweight", "low"].includes((r.status || "").toLowerCase()));
    }, [ciq]);

    /* ─── Find Date for Critical Alert ─── */
    const findDateForCriticalValue = useCallback((label, sourceNodeId) => {
        if (!label) return null;
        const lowerLabel = label.toLowerCase();
        
        let bestLabDate = null;
        let latestMs = 0;
        // 1. Check labPanels
        for (const panel of labPanels) {
            for (const test of (panel.tests || [])) {
                const tn = (test.testname || test.name || "").toLowerCase();
                if (!tn) continue;
                if (lowerLabel.includes(tn) || tn.includes(lowerLabel)) {
                    const resultDateStr = test.results?.[0]?.date;
                    if (resultDateStr) {
                        const ms = dayjs(resultDateStr).valueOf();
                        if (ms > latestMs) {
                            latestMs = ms;
                            bestLabDate = dayjs(resultDateStr).format("MMM D");
                        }
                    }
                }
            }
        }
        if (bestLabDate) return bestLabDate;

        // 2. Check vitals
        if (vitals && vitals.length > 0) {
            const latestVital = vitals[0];
            const dStr = dayjs(latestVital.date || latestVital.createddate).format("MMM D");
            if (lowerLabel.includes("weight") || lowerLabel.includes("wt") || lowerLabel.includes("bmi")) return dStr;
            if (lowerLabel.includes("blood pressure") || lowerLabel.includes("bp") || lowerLabel.includes("systolic") || lowerLabel.includes("diastolic")) return dStr;
            if (lowerLabel.includes("pulse") || lowerLabel.includes("heart rate")) return dStr;
            if (lowerLabel.includes("spo2") || lowerLabel.includes("oxygen")) return dStr;
        }

        // 3. Fallback: check documents based on sourceNodeId
        if (sourceNodeId && documents && documents.length > 0) {
            const docMatch = documents.find(d => 
                (d.label === sourceNodeId) || 
                (d.documentdescription === sourceNodeId) ||
                (d.encounterdocumentid == sourceNodeId) ||
                (d.imagingresultid == sourceNodeId)
            );
            if (docMatch) return dayjs(docMatch.createddate || docMatch.createddatetime).format("MMM D");
        }

        return null;
    }, [labPanels, vitals, documents]);

    const careGaps = ciq?.care_intelligence?.care_gaps || [];
    const suggestions = ciq?.care_intelligence?.suggestions || [];
    const riskFactors = ciq?.risk_radar?.risk_factors || [];
    const followUps = ciq?.followup_central?.items || [];

    // Extract pending results and send to parent NavFrame via callback
    useEffect(() => {
        const items = ciq?.pending_results?.items || [];
        const filtered = items.filter(pr => pr.order);
        if (onPendingResults) onPendingResults(filtered);
    }, [ciq, onPendingResults]);
    const suggestedOrders = ciq?.care_intelligence?.suggested_orders || [];

    // Merge CareIQ suggested orders with LiveScribe suggested orders
    const mergedSuggestedOrders = useMemo(() => {
        const ciqOrders = suggestedOrders.map((o) => ({
            order: o.order || o.description || "",
            order_type: o.order_type || o.ordertype,
            detail: o.detail || "",
            guideline_reference: o.guideline_reference,
            source: "careiq",
        }));
        const lsOrders = (liveScribeOrders || []).map((entry) => ({
            order: typeof entry === "string" ? entry : entry?.order_name || entry?.order || "",
            order_type: typeof entry === "string" ? null : (entry?.order_type || entry?.ordertype),
            detail: typeof entry === "string" ? "" : (entry?.details || entry?.detail || ""),
            source: "livescribe",
        }));
        // De-duplicate by order name
        const seen = new Set();
        const merged = [];
        for (const o of [...ciqOrders, ...lsOrders]) {
            const key = (o.order || "").toLowerCase().trim();
            if (key && !seen.has(key)) {
                seen.add(key);
                merged.push(o);
            }
        }
        return merged;
    }, [suggestedOrders, liveScribeOrders]);

    // Map AI-emitted order_type ("prescription", "lab", etc.) to the practice's
    // ordertype strings used by the orders dashboard.
    // - Direct map handles known canonical values
    // - When order_type is empty/null/unknown, we fall back to a NAME-BASED
    //   heuristic that recognises drug-class words ("Antibiotics",
    //   "Vitamin D", "Statin", etc.) and routes them to Prescriptions.
    //   Without this, the AI's underspecified order_type silently buckets
    //   meds into Procedures/Tests on the dashboard.
    const mapOrderType = (type, orderName = "") => {
        const normalised = String(type || "").toLowerCase().trim();
        const map = {
            lab: "Labs",
            labs: "Labs",
            imaging: "Diagnostic Tests",
            "diagnostic test": "Diagnostic Tests",
            "diagnostic tests": "Diagnostic Tests",
            procedure: "Procedures/Tests",
            procedures: "Procedures/Tests",
            "procedures/tests": "Procedures/Tests",
            referral: "Referrals",
            referrals: "Referrals",
            prescription: "Prescriptions",
            prescriptions: "Prescriptions",
            rx: "Prescriptions",
            medication: "Prescriptions",
            medications: "Prescriptions",
            med: "Prescriptions",
            meds: "Prescriptions",
            drug: "Prescriptions",
            drugs: "Prescriptions",
            antibiotic: "Prescriptions",
            antibiotics: "Prescriptions",
            antiviral: "Prescriptions",
            statin: "Prescriptions",
            injection: "Injections",
            injections: "Injections",
            shot: "Injections",
            vitamin: "Vitamin",
            vitamins: "Vitamin",
            supplement: "Vitamin",
            supplements: "Vitamin",
        };
        if (map[normalised]) return map[normalised];

        // Name-based heuristic for empty/unknown order_type — routes
        // drug-class generics like "Antibiotics", "Cough syrup" to Prescriptions
        // rather than the wrong default of Procedures/Tests.
        const name = String(orderName || "").toLowerCase();
        const drugLikeKeywords = [
            "antibiotic", "antiviral", "antifungal", "statin", "ssri", "snri",
            "nsaid", "steroid", "prescription", "syrup", "tablet", "capsule",
            "ointment", "cream", "inhaler", "drops",
            "amoxicillin", "azithromycin", "augmentin", "ibuprofen",
            "acetaminophen", "tylenol", "advil", "metformin", "atorvastatin",
            "lisinopril", "amlodipine", "losartan", "sertraline", "omeprazole",
            "pantoprazole", "albuterol", "prednisone", "gabapentin",
        ];
        if (drugLikeKeywords.some((k) => name.includes(k))) return "Prescriptions";

        const vitaminKeywords = ["vitamin", "vit b", "vit d", "biotin", "folate", "iron", "magnesium"];
        if (vitaminKeywords.some((k) => name.includes(k))) return "Vitamin";

        const labKeywords = ["panel", "lipid", "cbc", "cmp", "bmp", "a1c", "tsh", "psa", "ua", "urinalysis"];
        if (labKeywords.some((k) => name.includes(k))) return "Labs";

        const imagingKeywords = ["x-ray", "xray", "ultrasound", "ct ", "mri", "echo", "ekg", "ecg", "stress test", "pft", "sonogram"];
        if (imagingKeywords.some((k) => name.includes(k))) return "Diagnostic Tests";

        const referralKeywords = ["referral", "refer to", "consult"];
        if (referralKeywords.some((k) => name.includes(k))) return "Referrals";

        const injectionKeywords = ["injection", "shot", "inj "];
        if (injectionKeywords.some((k) => name.includes(k))) return "Injections";

        return "Procedures/Tests";
    };

    // ── Action handlers for suggested items ──
    const handleAddSuggestedOrder = useCallback(async (orderOrName) => {
        // Accept either a string (legacy callers) or an object { order, order_type, ... }
        const orderName = typeof orderOrName === "string"
            ? orderOrName
            : (orderOrName?.order || orderOrName?.order_name || "");
        const rawType = typeof orderOrName === "string" ? null : orderOrName?.order_type;
        const ordertype = mapOrderType(rawType, orderName);

        if (!orderName || addedOrders.has(orderName)) return;
        setAddedOrders((prev) => new Set([...prev, orderName]));
        // Strip leading "Order " prefix for cleaner display
        const cleanName = orderName.replace(/^Order\s+/i, "");
        // Optimistic local update for immediate UI feedback
        setCurrentOrders((prev) => [
            ...prev,
            { ordername: cleanName, ordertype, status: "CREATED", _added: true },
        ]);

        // Persist to database via orders/create API
        try {
            const userConfig = JSON.parse(localStorage.getItem("userConfig") || "{}");
            await careiqapi.post(
                `/v1/${practiceId}/patient/${patientId}/orders/create`,
                {
                    patient_name: dataId?.firstname + " " + dataId?.lastname,
                    createdby: userConfig?.username || "",
                    orderdate: chartOrderDate,
                    ordername: cleanName,
                    ordertype,
                    tov: "",
                    lov: 0,
                    diagnosis: [],
                    appointmentid: dataId?.appointmentid,
                    // v2 gateway rejects "" here (expects int); 0 = no encounter
                    encounterid: Number(chartEncounterId) || 0,
                    provider: chartProviderName,
                    token_no: 0,
                    confirm_status: "C",
                    update_status: "C",
                    confirm_status: "C", // v2 stores this verbatim; NULL breaks confirm/schedule flows
                },
            );
            console.log("[CareIQ] Persisted suggested order:", cleanName);
            // Dashboard cards carry an order summary; without this nudge the
            // new order only appears after a full page reload (QA 2026-08-19).
            eventBus.emit("refreshDashboard");
            // Re-fetch from DB to get accurate state
            try {
                const raw = await careiqapi.post(`/v1/${practiceId}/orders/get`, {
                    patientid: patientId,
                    orderdate: chartOrderDate,
                });
                const d = raw?.data || raw;
                const orders = Array.isArray(d) ? d : (d?.orders || d?.data || []);
                setCurrentOrders(orders);
            } catch (_) { /* swallow; optimistic update is already shown */ }
        } catch (err) {
            console.error("[CareIQ] Failed to persist suggested order:", err?.message);
        }
    }, [addedOrders, patientId, dataId, chartOrderDate, chartEncounterId, chartProviderName]);

    const handleConfirmDx = useCallback(async (dx) => {
        const key = dx.description || dx.icd10_code || "";
        if (confirmedDx.has(key)) return;
        setConfirmedDx((prev) => new Set([...prev, key]));

        try {
            // Call the orders/confirm endpoint to persist the diagnosis.
            // createdby comes from the redux user config — the old
            // localStorage read returned "" and the confirm 400'd.
            await careiqapi.post(
                `/v1/${practiceId}/patient/${patientId}/orders/confirm`,
                {
                    patient_name: dataId?.firstname + " " + dataId?.lastname,
                    createdby: userConfig?.username || userConfig?.config?.username || "care-ui",
                    orderdate: chartOrderDate,
                    tov: "",
                    lov: 0,
                    diagnosis: [dx.icd10_code].filter(Boolean),
                    appointmentid: dataId?.appointmentid,
                    encounterid: chartEncounterId,
                    token_no: 0,
                    confirm_status: "C",
                    update_status: "C",
                },
            );
            console.log("[CareIQ] Confirmed Dx:", dx.icd10_code, dx.description);
            // Refresh orders list so confirmed Dx persists on reopen
            eventBus.emit("fetchSummary");
        } catch (err) {
            console.error("[CareIQ] Failed to confirm Dx:", err?.message);
        }
    }, [confirmedDx, patientId, dataId, chartOrderDate, chartEncounterId, userConfig]);

    // Set default insights tab based on available data
    useEffect(() => {
        if (careGaps.length > 0) setInsightsTab("gaps");
        else if (suggestions.length > 0) setInsightsTab("recs");
        else if (riskFactors.length > 0) setInsightsTab("risks");
    }, [careGaps.length, suggestions.length, riskFactors.length]);

    /* ── Order edit/delete handlers ── */
    const refetchCurrentOrders = useCallback(async () => {
        try {
            const raw = await careiqapi.post(`/v1/${practiceId}/orders/get`, {
                patientid: patientId,
                orderdate: chartOrderDate,
            });
            const d = raw?.data || raw;
            const orders = Array.isArray(d) ? d : (d?.orders || d?.data || []);
            setCurrentOrders(orders);
        } catch (_) { /* swallow */ }
    }, [patientId, chartOrderDate]);

    const handleEditOrderClick = useCallback((order) => {
        setEditingOrder(order);
        setEditingOrderName(order.ordername || order.description || order.name || "");
        setConfirmDeleteOrder(null);
    }, []);

    const handleSaveOrderName = useCallback(async () => {
        if (!editingOrder || !editingOrderName.trim()) return;
        setEditingSaving(true);
        try {
            await careiqapi.put(
                `/v1/${practiceId}/orders/${patientId}/update-visit-order/${editingOrder.id}`,
                { ordername: editingOrderName.trim() }
            );
            // Optimistic local update
            setCurrentOrders((prev) =>
                prev.map((o) => o.id === editingOrder.id ? { ...o, ordername: editingOrderName.trim() } : o)
            );
            setEditingOrder(null);
            setEditingOrderName("");
            // Re-fetch for accuracy
            await refetchCurrentOrders();
            eventBus.emit("fetchSummary");
        } catch (err) {
            console.error("[CareIQ] Failed to update order:", err?.message);
        } finally {
            setEditingSaving(false);
        }
    }, [editingOrder, editingOrderName, patientId, refetchCurrentOrders]);

    const handleDeleteOrder = useCallback(async (order) => {
        setEditingSaving(true);
        try {
            // orderdate = the date this list was loaded for (chartOrderDate), never
            // today: the server picks orders_master or orders_master_history from
            // the date, and their ids collide, so a past-visit chart's history id
            // sent with today's date names another patient's current order.
            // patientid scopes the delete to this patient where the server supports it.
            await careiqapi.delete(
                `/v1/${practiceId}/orders/delete/order/${order.id}`,
                {
                    data: {
                        orderdate: chartOrderDate,
                        ...(patientId ? { patientid: Number(patientId) } : {}),
                    },
                }
            );
            // Optimistic local removal
            setCurrentOrders((prev) => prev.filter((o) => o.id !== order.id));
            setEditingOrder(null);
            setEditingOrderName("");
            setConfirmDeleteOrder(null);
            // Re-fetch for accuracy
            await refetchCurrentOrders();
            eventBus.emit("fetchSummary");
        } catch (err) {
            console.error("[CareIQ] Failed to delete order:", err?.message);
        } finally {
            setEditingSaving(false);
        }
    }, [patientId, chartOrderDate, refetchCurrentOrders]);

    /* ─── Filtered current orders (exclude diagnosis-like entries) ─── */
    const filteredOrders = useMemo(() =>
        currentOrders.filter(o => {
            const name = (o.ordername || o.name || "").trim();
            // Filter out entries whose name looks like an ICD-10 code
            if (ICD10_PATTERN.test(name)) return false;
            // Filter out entries with ordertype "Diagnosis" or "diagnosis"
            if ((o.ordertype || "").toLowerCase() === "diagnosis") return false;
            return true;
        })
        , [currentOrders]);

    /* ─── Grouped current orders ─── */
    const groupedOrders = useMemo(() => groupOrdersByType(filteredOrders), [filteredOrders]);

    /* ─── Build unified timeline ─── */
    const timelineEvents = useMemo(() => {
        const events = [];
        // Visit statuses that carry no clinical value — keep them off the
        // timeline (doctor request, Jun 2026). Backend maps FHIR
        // cancelled/entered-in-error -> CANCELLED in mirror_encounters_for_kg.
        const HIDDEN_VISIT_STATUSES = new Set([
            "CANCELLED", "CANCELED", "ENTERED-IN-ERROR",
            "NO-SHOW", "NOSHOW", "NO SHOW", "X",
        ]);
        // Encounter-type classification for ancillary clutter (doctor request,
        // Jun 2026). encountertype / encountervisitname are comma-joined strings
        // that can bundle several things into one encounter, so we use
        // KEEP-PRECEDENCE: an encounter is hidden ONLY when it is purely
        // ancillary (injection / immunization / lab draw) with no real-visit
        // component. A real follow-up that also gave a B12 shot, or a patient
        // with a separate same-day injection encounter, is never lost.
        const REAL_VISIT_RE = /OFFICE\s*\/?\s*OUTPATIENT VISIT|OFFICE VISIT|FOLLOW\s*UP|NEW PT|NEW PATIENT|WALK[\s-]*IN/i;
        const ANCILLARY_VISIT_RE = /\bINJ\b|INJECT|IMMUNIZ|VACCIN|SWAB|BLOOD TEST|GLUCOSE|VITAMIN|\bASSAY\b|THER\/PROPH|VENIPUNCTURE/i;
        encounters.forEach((enc) => {
            const status = (enc.status || enc.appointmentstatus || "").toString().trim().toUpperCase();
            if (HIDDEN_VISIT_STATUSES.has(status)) return; // cancelled / errored / no-show
            if (enc.has_clinical_content === false) return; // backend-confirmed blank note (no HPI / A&P)
            const encText = `${enc.encountertype || ""} ${enc.encountervisitname || ""} ${enc.appointmenttypename || ""}`;
            const looksAncillary = !REAL_VISIT_RE.test(encText) && ANCILLARY_VISIT_RE.test(encText);
            const d = dayjs(enc.startdate || enc.date || enc.encounterdate);
            if (d.isValid()) {
                // Does this visit have a handwritten note? Match by encounterid,
                // else a note created 0–7 days after the visit (mirrors the
                // per-visit modal's matching so the icon agrees with the tab).
                const encId = String(enc.encounterid || "");
                const hasHandwritten = handwrittenDocs.some((hw) => {
                    if (encId && String(hw.encounterid || "") === encId) return true;
                    const hwDt = dayjs(hw.createddatetime || hw.createddate);
                    if (!hwDt.isValid()) return false;
                    const diff = hwDt.diff(d, "day");
                    return diff >= 0 && diff <= 7;
                });
                // Ancillary-looking visits are hidden ONLY when nothing clinical
                // hangs off them. A lab-named visit with a handwritten note or
                // confirmed clinical content is a real visit (QA 2026-08-18:
                // "VITAMIN B12, ... ASSAY OF MAGNESIUM" hid a documented visit).
                if (looksAncillary && !hasHandwritten && enc.has_clinical_content !== true) return;
                // Label = provider name only (user 2026-09-02: the VISIT
                // badge already says what it is — "Office Visit · M. Pa…"
                // just truncated the useful part). Full name fits now;
                // visit-type text falls back only when no provider resolved.
                const encProv = `${enc.providerfirstname || ""} ${enc.providerlastname || ""}`.trim();
                events.push({ date: d, dateStr: d.format("MMM D"), yearBadge: d.year() !== dayjs().year() ? String(d.year()) : null, label: encProv || enc.appointmenttypename || "Office Visit", type: "visit", dotColor: "normal", hasHandwritten, raw: enc });
            }
        });

        // Visits documented ONLY by a BlinkScribe note (no Athena encounter —
        // migrated/legacy or never-checked-in visits): add a synthetic visit
        // entry so the timeline reflects them. Real encounters on the same
        // date win; one entry per date.
        const encounterDates = new Set(events.filter((e) => e.type === "visit").map((e) => e.date.format("YYYY-MM-DD")));
        const bsSeenDates = new Set();
        bsVisits.forEach((rec) => {
            const d = dayjs(rec.ts || rec.created_at || rec.date);
            if (!d.isValid()) return;
            const key = d.format("YYYY-MM-DD");
            if (encounterDates.has(key) || bsSeenDates.has(key)) return;
            bsSeenDates.add(key);
            // Recorder wins over the appointment's scheduled provider —
            // recording/details supplies both since 2026-09-02. Label =
            // provider name only, matching the encounter rows.
            const bsProvFull = (rec.recorded_by || rec.providername || "").trim();
            events.push({
                date: d,
                dateStr: d.format("MMM D"),
                yearBadge: d.year() !== dayjs().year() ? String(d.year()) : null,
                label: bsProvFull || "Office Visit (BlinkScribe note)",
                type: "visit",
                dotColor: "normal",
                hasHandwritten: false,
                raw: { bsOnly: true, startdate: d.format("YYYY-MM-DD"), encounterid: "", appointmenttypename: "Office Visit (BlinkScribe note)" },
            });
        });

        const labsByDate = {};
        labPanels.forEach((panel) => {
            const pName = panel.panel_name || panel.panelname || "";
            (panel.tests || []).forEach((test) => {
                // Bucket EACH result by its own date, not just the latest result.
                // Otherwise a test with both a 2025 and 2026 reading only ever
                // appears under the 2026 date, and clicking the 2025 timeline row
                // (created from a different test) renders 2026 values.
                (test.results || []).forEach((result) => {
                    if (!result) return;
                    const d = dayjs(result.date);
                    if (!d.isValid()) return;
                    const key = d.format("YYYY-MM-DD");
                    if (!labsByDate[key]) labsByDate[key] = { date: d, tests: [], panels: new Set() };
                    // Note: field is still called `latestResult` for downstream
                    // compatibility; semantically it's "the result for this date".
                    labsByDate[key].tests.push({ ...test, panelname: pName, latestResult: result });
                    if (pName) labsByDate[key].panels.add(pName);
                });
            });
        });
        Object.values(labsByDate).forEach((group) => {
            const panelNames = [...group.panels];
            const label = panelNames.length > 0 ? panelNames.join("/") : "Labs";
            const hasAbnormal = group.tests.some((t) => isOutOfRange(t.latestResult?.value, t.refrange || t.latestResult?.refrange));
            // Build searchable text from all test names + values for deep search
            const searchText = group.tests.map((t) => {
                const name = t.testname || t.name || "";
                const pn = t.panelname || "";
                const val = t.latestResult?.value || "";
                const units = t.latestResult?.units || t.latestResult?.unit || "";
                return `${name} ${pn} ${val} ${units}`;
            }).join(" | ");
            events.push({ date: group.date, dateStr: group.date.format("MMM D"), yearBadge: group.date.year() !== dayjs().year() ? String(group.date.year()) : null, label, type: "lab", dotColor: hasAbnormal ? "abnormal" : "normal", searchText, raw: { dateStr: group.date.format("MMM D, YYYY"), tests: group.tests } });
        });

        // Every imaging result also comes back in the clinical list as its FHIR
        // "Cardiology/Radiology Report" twin — skip the twin while the imaging
        // event itself is on the timeline (see Document/documentTwins.js).
        const shownImagingIds = timelineDocs.includes("imagingresult")
            ? imagingIdSet(documents.filter((x) => x?.imagingresultid))
            : new Set();
        [...documents, ...encDocuments].forEach((doc) => {
            const d = dayjs(doc.createddate || doc.createddatetime);
            if (!d.isValid()) return;
            if (isImagingReportTwin(doc, shownImagingIds)) return;
            // Category-aware id resolution: clinical docs use
            // clinicaldocumentid, encounter docs use encounterdocumentid,
            // imaging uses imagingresultid. Without the clinicaldocumentid
            // fallback, hospital discharge summaries surfaced no label
            // beyond Athena's catch-all 'clinical document'.
            const docId = doc.imagingresultid || doc.encounterdocumentid || doc.clinicaldocumentid;
            const kgLabel = docId ? docLabels[docId] : null;
            const isClinical = doc.documentclass === "CLINICALDOCUMENT" || !!doc.clinicaldocumentid;
            const isEncounter = doc.documentclass === "ENCOUNTERDOCUMENT" || !!doc.encounterdocumentid;
            // Preference gate (⚙ modal / practice default): drop categories the
            // user doesn't want on their timeline.
            const docCategory = isEncounter ? "encounterdocument" : (isClinical ? "clinicaldocument" : "imagingresult");
            if (!timelineDocs.includes(docCategory)) return;
            const fallbackType = isClinical ? "Document" : (isEncounter ? "Note" : "Imaging");
            const labelText = kgLabel || doc.label || doc.documentdescription || doc.description || fallbackType;
            // Doctor request June 2026: keep the timeline focused on summative
            // documents (discharge summaries, H&P, imaging reports, FNA path,
            // etc.). Filter out generic "Progress Note" entries — they are
            // the common in-office note and surface elsewhere in the chart,
            // not as a standalone timeline event. Suspended when the user has
            // explicitly opted INTO progress/encounter notes via preferences.
            if (!wantsEncDocs && /^\s*progress\s*note\s*$/i.test(labelText)) return;
            // Categorise visually: imaging keeps its existing pill; the new
            // clinical + encounter docs share a 'document' pill labelled DOC.
            const eventType = (isClinical || isEncounter) ? "document" : "imaging";
            events.push({
                date: d,
                dateStr: d.format("MMM D"),
                yearBadge: d.year() !== dayjs().year() ? String(d.year()) : null,
                label: labelText,
                type: eventType,
                dotColor: "pending",
                raw: { ...doc, patientid: patientId },
            });
        });

        events.sort((a, b) => b.date.valueOf() - a.date.valueOf());
        return events;
    }, [encounters, bsVisits, labPanels, documents, encDocuments, docLabels, patientId, handwrittenDocs, timelineDocs, wantsEncDocs]);

    const filteredTimeline = useMemo(() => {
        if (!timelineSearch.trim()) return timelineEvents;
        const q = timelineSearch.toLowerCase();
        return timelineEvents.filter((ev) => ev.label.toLowerCase().includes(q) || ev.dateStr.toLowerCase().includes(q) || ev.type.includes(q) || (ev.searchText && ev.searchText.toLowerCase().includes(q)));
    }, [timelineEvents, timelineSearch]);

    const visibleTimeline = timelineExpanded ? filteredTimeline : filteredTimeline.slice(0, 15);

    // One grey provenance line while a timeline document list is being
    // refreshed on the server ("Documents as of Sep 17 · updating"). Only the
    // categories this user shows on the timeline count.
    const docsAsOfLine = useMemo(() => {
        const all = { ...docsMeta, encounterdocument: wantsEncDocs ? encDocsMeta : null };
        return documentsAsOfLine(Object.fromEntries(
            Object.entries(all).filter(([cat]) => timelineDocs.includes(cat))));
    }, [docsMeta, encDocsMeta, wantsEncDocs, timelineDocs]);
    const timelineBusy = timelineLoading || docsLoading || (wantsEncDocs && encDocsLoading);

    const handleTimelineClick = useCallback((ev) => {
        if (!ev.raw) return;
        switch (ev.type) {
            case "visit": setVisitModal(ev.raw); break;
            case "lab": setLabModal(ev.raw); break;
            // Both 'imaging' and 'document' (clinical / encounter docs added
            // in the unified-timeline change) open the same doc viewer modal.
            // The viewer routes the /sync/0 call by whichever id field the
            // raw doc carries (imagingresultid / clinicaldocumentid /
            // encounterdocumentid).
            case "imaging":
            case "document":
                setDocModal(ev.raw);
                setDocModalOpen(true);
                break;
            default: setModalItem(ev.raw); break;
        }
    }, []);

    const handleSwitchToLabs = useCallback(() => {
        setLabModal(null);
        if (onSwitchTab) onSwitchTab(1);
    }, [onSwitchTab]);

    /* ─── Render ─── */

    // Timeline type filter (dropdown). Documents (clinical / encounter docs)
    // only get an option when some are on the timeline.
    const tlCounts = timelineEvents.reduce((m, ev) => { m[ev.type] = (m[ev.type] || 0) + 1; return m; }, {});
    const tlChips = [
        { id: "all", label: "All types" },
        { id: "visit", label: "Visits", dot: "#8a93a5" },
        { id: "lab", label: "Labs", dot: "#2a8c82" },
        { id: "imaging", label: "Imaging", dot: "#6b7a96" },
        tlCounts.document ? { id: "document", label: "Docs", dot: "#a07514" } : null,
    ].filter(Boolean);
    const tlShown = tlFilter === "all" ? filteredTimeline : filteredTimeline.filter((ev) => ev.type === tlFilter);
    const tlVisible = timelineExpanded ? tlShown : tlShown.slice(0, 15);

    // Critical values split into labs and imaging, the way the card reads.
    const isImagingAlert = (r) => /imag|radiol|x-?ray|\bct\b|mri|ultrasound|mammo|dexa|echo/i
        .test(`${r.category || ""} ${r.source_type || ""} ${r.type || ""} ${r.source_node_id || ""}`);
    const cvImaging = criticalAlerts.filter(isImagingAlert);
    const cvLabs = criticalAlerts.filter((r) => !isImagingAlert(r));
    const cvFlag = (r) => {
        const s = (r.status || "").toLowerCase();
        if (s === "high") return "H";
        if (s === "low") return "L";
        if (s === "abnormal") return "Ab";
        return s.charAt(0).toUpperCase();
    };
    const renderCv = (r, i) => (
        <div key={i} className="bmch-cv" onClick={() => setModalItem(r)}>
            <span className="bmch-cv-name">{r.label}</span>
            <span className="bmch-cv-date">{findDateForCriticalValue(r.label, r.source_node_id) || ""}</span>
            <span className="bmch-cv-val">{r.value || "—"}</span>
            <span className={`bmch-cv-flag${(r.status || "").toLowerCase() === "low" ? " low" : ""}`}>{cvFlag(r)}</span>
        </div>
    );

    const isDone = (s) => ["completed", "complete"].includes((s || "").toLowerCase());
    const tagClass = (s) => `bmch-tag t-${String(s || "").toLowerCase().replace(/[^a-z]+/g, "-")}`;
    const showSuggested = sectionOn("suggested_diagnoses") || sectionOn("suggested_orders");
    const showInsights = sectionOn("care_gaps") || sectionOn("recommendations") || sectionOn("risk_factors");

    // Current DX: redux diagnoses, plus diagnosis rows from the Orders
    // module, plus suggested DX confirmed here.
    const reduxDxKeys = new Set(reduxDiagnoses.map(c => c.condition_name || c.icd10 || ""));
    const ordersDx = (reduxOrdersData || []).filter(o => (o.ordertype || "").toLowerCase() === "diagnosis").map(o => ({
        condition_name: o.ordername || o.description || o.name || "",
        icd10: o.icd10 || o.code || "",
    })).filter(d => d.condition_name && !reduxDxKeys.has(d.condition_name));
    const localConfirmedDx = aiDiagnoses
        .filter(dx => confirmedDx.has(dx.description || dx.icd10_code || ""))
        .filter(dx => !reduxDxKeys.has(dx.description || ""))
        .map(dx => ({ condition_name: dx.description, icd10: dx.icd10_code }));
    const allCurrentDx = [...reduxDiagnoses, ...ordersDx, ...localConfirmedDx];

    const timelinePanel = showTimeline && (
        <aside className="bmch-tl" ref={sidebarRef}>
            <div className="bmch-tl-head">
                <History />
                <span className="bmch-tl-title">Patient timeline</span>
                {(timelineBusy || allLabsLoading) && <span className="bmch-spin" />}
                <span style={{ marginLeft: "auto" }} />
                <button
                    type="button"
                    className="bmch-tl-iconbtn"
                    onClick={() => { setTlDraft(timelineDocs); setTlPrefsOpen(true); }}
                    title="Choose which documents appear on the timeline"
                    aria-label="Timeline document preferences"
                >
                    <Settings size={16} />
                </button>
                {onCloseTimeline && (
                    <button type="button" className="bmch-tl-iconbtn" onClick={onCloseTimeline} title="Close timeline" aria-label="Close timeline">
                        <X size={18} />
                    </button>
                )}
            </div>
            <div className="bmch-tl-tools">
                <label className="bmch-tl-search">
                    <Search />
                    <input
                        type="text"
                        placeholder="Search, e.g. A1C or CBC"
                        value={timelineSearch}
                        onChange={(e) => { setTimelineSearch(e.target.value); if (e.target.value.trim().length >= 2) loadAllLabs(); }}
                    />
                </label>
                {(() => {
                    const cur = tlChips.find((c) => c.id === tlFilter) || tlChips[0];
                    return (
                        <div className="bmch-tl-type" ref={tlTypeRef}>
                            <button
                                type="button"
                                className={`bmch-tl-type-btn${tlFilter !== "all" ? " active" : ""}`}
                                aria-haspopup="listbox"
                                aria-expanded={tlTypeOpen}
                                onClick={() => setTlTypeOpen((v) => !v)}
                            >
                                {cur.dot && <i style={{ "--dot": cur.dot }} />}
                                {tlFilter === "all" ? "All" : cur.label}
                                <ChevronDown size={14} />
                            </button>
                            {tlTypeOpen && (
                                <div className="bmch-tl-type-menu" role="listbox">
                                    {tlChips.map((c) => (
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={tlFilter === c.id}
                                            key={c.id}
                                            className={`bmch-tl-type-opt${tlFilter === c.id ? " on" : ""}`}
                                            onClick={() => { setTlFilter(c.id); setTlTypeOpen(false); }}
                                        >
                                            <i style={{ "--dot": c.dot || "transparent" }} />
                                            <span>{c.label}</span>
                                            <em>{c.id === "all" ? timelineEvents.length : (tlCounts[c.id] || 0)}</em>
                                            {tlFilter === c.id && <Check size={14} />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>
            {docsAsOfLine && (
                <div className="bmch-tl-asof" style={{ fontSize: "10.5px", color: BRAND.slateMuted, padding: "0 0 6px", lineHeight: 1.4 }}>
                    {docsAsOfLine}
                </div>
            )}
            <div className="bmch-tl-scroll">
                {tlShown.length === 0 ? (
                    // still loading: the header spinner says so; "No events" would be wrong yet
                    timelineBusy ? null : <div className="bmch-empty" style={{ padding: "18px 0" }}>No events found</div>
                ) : (() => {
                    const q = timelineSearch.trim().toLowerCase();
                    return tlVisible.map((ev, i) => {
                        // Small divider where the year changes (and above the
                        // first row when it isn't this year).
                        const yr = ev.date.year();
                        const prevYr = i > 0 ? tlVisible[i - 1].date.year() : dayjs().year();
                                // When searching labs, show matching test names + values inline
                                let matchedTests = null;
                                if (q && ev.type === "lab" && ev.raw?.tests) {
                                    const hits = ev.raw.tests.filter((t) => {
                                        const name = (t.testname || t.name || "").toLowerCase();
                                        const pn = (t.panelname || "").toLowerCase();
                                        return name.includes(q) || pn.includes(q);
                                    });
                                    if (hits.length > 0) {
                                        matchedTests = hits.map((t) => {
                                            const name = t.testname || t.name || "";
                                            const val = t.latestResult?.value || "—";
                                            const units = t.latestResult?.units || t.latestResult?.unit || "";
                                            const refrange = t.refrange || t.latestResult?.refrange || "";
                                            const abnormal = isOutOfRange(t.latestResult?.value, refrange);
                                            return { name, val, units, refrange, abnormal };
                                        });
                                    }
                                }
                                const abnormal = ev.dotColor === "abnormal";
                                return (
                                    <React.Fragment key={i}>
                                    {yr !== prevYr && <div className="bmch-tl-year"><span>{yr}</span></div>}
                                    <div className="bmch-tl-ev" onClick={() => handleTimelineClick(ev)}>
                                        <div className="bmch-tl-ev-main">
                                            {/* One line: date · title · type · abnormal · handwritten */}
                                            <div className="bmch-tl-line">
                                                <span className={`bmch-tl-date${abnormal ? " hot" : ""}`} title={ev.date.format("dddd, MMMM D, YYYY")}>
                                                    {ev.date.format("MMM D")}
                                                </span>
                                                <span className="bmch-tl-ev-title" title={ev.label}>{ev.label}</span>
                                                <span className="bmch-tl-abn-cell">{abnormal && <span className="bmch-tl-abn" title="Abnormal">Abn</span>}</span>
                                                <span className={tagClass(ev.type)}>{ev.type === "visit" ? visitPillLabel(ev.raw) : pillLabel(ev.type)}</span>
                                                <span className="bmch-tl-hw">
                                                    {ev.type === "visit" && ev.hasHandwritten && (
                                                        <span title="Handwritten note on file" aria-label="Handwritten note on file">
                                                            <Pencil size={12} />
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                            {matchedTests && (
                                                <div className="bmch-tl-hits">
                                                    {matchedTests.map((mt, j) => (
                                                        <div key={j} className={`bmch-tl-hit${mt.abnormal ? " abn" : ""}`}>
                                                            <span style={{ fontWeight: 500 }}>{mt.name}</span>
                                                            <span style={{ fontWeight: 600 }}>{mt.val}</span>
                                                            {mt.units && <span style={{ color: "#8a93a5" }}>{mt.units}</span>}
                                                            {mt.refrange && <span style={{ color: "#8a93a5", fontSize: 12 }}>({mt.refrange})</span>}
                                                            {mt.abnormal && <span style={{ fontWeight: 600 }}>⚠</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    </React.Fragment>
                                );
                    });
                })()}
            </div>
            <div className="bmch-tl-foot">
                <span>{tlVisible.length} of {tlShown.length}</span>
                {tlShown.length > 15 && (
                    <button
                        type="button"
                        className="bmch-link"
                        onClick={() => { setTimelineExpanded(!timelineExpanded); if (!timelineExpanded) loadAllLabs(); }}
                    >
                        {timelineExpanded ? "Show less" : "Show all"}
                    </button>
                )}
            </div>
        </aside>
    );

    return (
        <div className="bmch bmch-ciq">
            <div className="bmch-ciq-main">
                {loading ? (
                    <div className="ciqt-loader">
                        <div className="ciqt-spinner" />
                        <span className="ciqt-loader-text">Loading CareIQ insights…</span>
                    </div>
                ) : (<>
                {/* ═══ DATE OF SERVICE ═══ */}
                <div className="bmch-dos">
                    <div>
                        <div className="bmch-dos-k">Date of service</div>
                        <div className="bmch-dos-v">
                            {chartOrderDate ? dayjs(chartOrderDate).format("MMMM D, YYYY") : "—"}
                            {chartProviderName && (
                                <span className="bmch-dos-prov"><Stethoscope />{providerDisplayName(chartProviderName)}</span>
                            )}
                        </div>
                    </div>
                    <div className="bmch-chips">
                        {visitTov && <span className="bmch-chip"><CalendarDays />{visitTov}</span>}
                        {onToggleTimeline && (
                            <button
                                type="button"
                                className={`bmch-chip-icon${showTimeline ? " on" : ""}`}
                                onClick={onToggleTimeline}
                                title={showTimeline ? "Hide patient timeline" : "Show patient timeline"}
                                aria-label={showTimeline ? "Hide patient timeline" : "Show patient timeline"}
                                aria-pressed={showTimeline}
                            >
                                <History />
                            </button>
                        )}
                    </div>
                </div>

                <div className="bmch-grid">
                    {/* ──── Suggested by CareIQ ──── */}
                    {showSuggested && (
                    <section className="bmch-card indigo">
                        <div className="bmch-card-head">
                            <span className="bmch-card-icon"><Sparkles /></span>
                            <span className="bmch-card-title">Suggested by CareIQ</span>
                            <span className="bmch-count">
                                {(sectionOn("suggested_diagnoses") ? aiDiagnoses.length : 0) + (sectionOn("suggested_orders") ? mergedSuggestedOrders.length : 0)}
                            </span>
                        </div>
                        <div className="bmch-card-body">
                            {sectionOn("suggested_diagnoses") && (<>
                                <div className="bmch-sub">Diagnoses</div>
                                {aiDiagnoses.length > 0 ? (
                                    <div>
                                        {aiDiagnoses.map((dx, i) => {
                                            const key = dx.description || dx.icd10_code || "";
                                            const isConfirmed = confirmedDx.has(key);
                                            return (
                                                <span key={i} className="bmch-dx">
                                                    {dx.icd10_code && <span className="bmch-dx-code">{dx.icd10_code}</span>}
                                                    {dx.description || "—"}
                                                    <button
                                                        type="button"
                                                        className="bmch-dx-act"
                                                        disabled={isConfirmed}
                                                        onClick={isConfirmed ? undefined : (e) => { e.stopPropagation(); handleConfirmDx(dx); }}
                                                        title={isConfirmed ? "Added" : "Add to current diagnoses"}
                                                    >
                                                        {isConfirmed ? "✓" : "+"}
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                ) : <div className="bmch-empty">No suggested diagnoses</div>}
                            </>)}

                            {sectionOn("suggested_orders") && (<>
                                <div className="bmch-sub">Orders</div>
                                {mergedSuggestedOrders.length > 0 ? mergedSuggestedOrders.map((o, i) => {
                                    const isAdded = addedOrders.has(o.order);
                                    const tag = o.order_type || o.category || o.ordertype || o.type;
                                    return (
                                        <div key={i} className="bmch-row" style={{ opacity: isAdded ? 0.55 : 1 }}>
                                            <div className="bmch-row-main" style={{ cursor: "pointer" }} onClick={() => setModalItem(o)}>
                                                {tag && <span className={tagClass(tag)} style={{ margin: "0 0 6px" }}>{tag}</span>}
                                                <div className="bmch-row-text">{o.order || "—"}</div>
                                            </div>
                                            <button
                                                type="button"
                                                className="bmch-btn"
                                                onClick={(e) => { e.stopPropagation(); handleAddSuggestedOrder(o); }}
                                                disabled={isAdded}
                                            >
                                                {isAdded ? <Check /> : <Plus />} {isAdded ? "Added" : "Add"}
                                            </button>
                                        </div>
                                    );
                                }) : <div className="bmch-empty">No suggested orders</div>}
                            </>)}
                        </div>
                    </section>
                    )}

                    {/* ──── Current orders ──── */}
                    <section className="bmch-card navy">
                        <div className="bmch-card-head">
                            <span className="bmch-card-icon"><ClipboardList /></span>
                            <span className="bmch-card-title">Current orders</span>
                            <span className="bmch-count">{filteredOrders.length}</span>
                            <div className="bmch-card-tools">
                                <button type="button" className="bmch-btn ghost" onClick={() => eventBus.emit("chartToggleOrders")}>
                                    Open orders
                                </button>
                            </div>
                        </div>
                        <div className="bmch-card-body">
                            <div className="bmch-sub">Diagnoses</div>
                            {allCurrentDx.length > 0 ? (
                                <div>
                                    {allCurrentDx.map((c, i) => (
                                        <span key={c.id || i} className="bmch-dx" style={{ paddingRight: 10 }}>
                                            {c.icd10 && <span className="bmch-dx-code">{c.icd10}</span>}
                                            {c.condition_name || "—"}
                                        </span>
                                    ))}
                                </div>
                            ) : <div className="bmch-empty">No current diagnoses</div>}

                            <div className="bmch-sub">Orders</div>
                            {ordersLoading ? (
                                <div className="bmch-empty">Loading orders…</div>
                            ) : filteredOrders.length > 0 ? filteredOrders.map((order, i) => {
                                const isEditing = editingOrder && editingOrder.id === order.id;
                                const isDeleting = confirmDeleteOrder && confirmDeleteOrder.id === order.id;
                                const name = order.ordername || order.description || order.name || "";
                                const type = (order.ordertype || "").toUpperCase();
                                if (!isEditing) {
                                    return (
                                        <div
                                            key={order.id || i}
                                            className="bmch-row click"
                                            onClick={() => handleEditOrderClick(order)}
                                            title="Click to edit"
                                            style={order._added ? { background: "#f1faf5" } : undefined}
                                        >
                                            <div className="bmch-row-main">
                                                <div className="bmch-row-title">{name || "—"}</div>
                                                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                                                    {order._added && <span style={{ color: BRAND.coral, display: "inline-flex" }} title="Added from CareIQ"><Sparkles size={13} /></span>}
                                                    {order.ordertype && <span className={tagClass(order.ordertype)} style={{ margin: 0 }}>{order.ordertype}</span>}
                                                    {type === "VISIT" && visitLovCode && (
                                                        <span className="bmch-tag t-lov" title="Level of Visit" style={{ margin: 0 }}>LOV {visitLovCode}</span>
                                                    )}
                                                    {order.instructions && order.instructions !== order.ordertype && (
                                                        <span className="bmch-row-sub" style={{ marginTop: 0 }}>{order.instructions}</span>
                                                    )}
                                                </div>
                                                {/* Athena appointment reason, shown under the visit line
                                                    the way athenaNet shows it under the appointment type. */}
                                                {type === "VISIT" && apptReason && (
                                                    <div className="bmch-row-sub" title={apptReason} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        Reason: {apptReason}
                                                    </div>
                                                )}
                                            </div>
                                            {order.status && (
                                                <span className={`bmch-status${isDone(order.status) ? " done" : ""}`}>
                                                    {isDone(order.status) && <Check />}
                                                    {order.status.charAt(0).toUpperCase() + order.status.slice(1).toLowerCase()}
                                                </span>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <div key={order.id || i} className="bmch-edit">
                                        {!isDeleting ? (<>
                                            <input
                                                autoFocus
                                                className="bmch-input"
                                                value={editingOrderName}
                                                onChange={(e) => setEditingOrderName(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === "Enter") handleSaveOrderName(); if (e.key === "Escape") { setEditingOrder(null); setEditingOrderName(""); } }}
                                            />
                                            {(order.ordertype || order.status) && (
                                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                                                    {order.ordertype && <span className={tagClass(order.ordertype)} style={{ margin: 0 }}>{order.ordertype}</span>}
                                                    {order.instructions && order.instructions !== order.ordertype && <span className="bmch-row-sub" style={{ marginTop: 0 }}>{order.instructions}</span>}
                                                    {order.status && <span className={`bmch-status${isDone(order.status) ? " done" : ""}`} style={{ marginLeft: "auto" }}>{order.status.charAt(0).toUpperCase() + order.status.slice(1).toLowerCase()}</span>}
                                                </div>
                                            )}
                                            <div className="bmch-edit-actions">
                                                <button
                                                    type="button"
                                                    className="bmch-btn solid"
                                                    onClick={handleSaveOrderName}
                                                    disabled={editingSaving || !editingOrderName.trim() || editingOrderName.trim() === name}
                                                >
                                                    <Save /> {editingSaving ? "Saving…" : "Save"}
                                                </button>
                                                <button type="button" className="bmch-btn danger" onClick={() => setConfirmDeleteOrder(order)} disabled={editingSaving}>
                                                    <Trash2 /> Delete
                                                </button>
                                                <button type="button" className="bmch-btn ghost" onClick={() => { setEditingOrder(null); setEditingOrderName(""); }}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </>) : (
                                            /* ── Delete confirmation ── */
                                            <div className="bmch-confirm">
                                                <div className="bmch-row-title">Delete this order?</div>
                                                <div className="bmch-row-sub">"{name}" will be permanently removed.</div>
                                                <div className="bmch-edit-actions">
                                                    <button type="button" className="bmch-btn danger" onClick={() => handleDeleteOrder(order)} disabled={editingSaving}>
                                                        <Trash2 /> {editingSaving ? "Deleting…" : "Yes, delete"}
                                                    </button>
                                                    <button type="button" className="bmch-btn ghost" onClick={() => setConfirmDeleteOrder(null)} disabled={editingSaving}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            }) : <div className="bmch-empty">No current orders</div>}
                        </div>
                    </section>

                    {/* ──── Follow-ups ──── */}
                    {sectionOn("follow_ups") && (
                    <section className="bmch-card teal">
                        <div className="bmch-card-head">
                            <span className="bmch-card-icon"><ListChecks /></span>
                            <span className="bmch-card-title">Follow-ups</span>
                            <span className="bmch-count">{followUps.length}</span>
                        </div>
                        <div className="bmch-card-body">
                            {followUps.length > 0 ? followUps.map((f, i) => {
                                const urgency = f.urgency || f.priority || "";
                                const level = urgency === "high" || urgency === "urgent" ? " high" : urgency === "low" ? " low" : "";
                                return (
                                    <div key={i} className="bmch-fu" onClick={() => setModalItem(f)}>
                                        <span className={`bmch-fu-mark${level}`} title={urgency ? `Priority: ${urgency}` : undefined} />
                                        <span className="bmch-row-text">{f.item || f.action || f.description || "—"}</span>
                                    </div>
                                );
                            }) : <div className="bmch-empty" style={{ paddingTop: 10 }}>No follow-ups pending</div>}
                        </div>
                    </section>
                    )}

                    {/* ──── Critical values ──── */}
                    {sectionOn("critical_values") && (
                    <section className="bmch-card orange">
                        <div className="bmch-card-head">
                            <span className="bmch-card-icon"><TriangleAlert /></span>
                            <span className="bmch-card-title">Critical values</span>
                            <span className={`bmch-count${criticalAlerts.length ? " hot" : ""}`}>{criticalAlerts.length}</span>
                        </div>
                        <div className="bmch-card-body">
                            {criticalAlerts.length === 0 && <div className="bmch-empty" style={{ paddingTop: 10 }}>No critical values</div>}
                            {cvLabs.length > 0 && (<>
                                <div className="bmch-sub">Labs · {cvLabs.length}</div>
                                {(cvExpanded ? cvLabs : cvLabs.slice(0, 6)).map(renderCv)}
                                {cvLabs.length > 6 && (
                                    <div className="bmch-more-row">
                                        <button type="button" className="bmch-link" onClick={() => setCvExpanded(!cvExpanded)}>
                                            {cvExpanded ? "Show less" : `Show all ${cvLabs.length} labs`}
                                        </button>
                                    </div>
                                )}
                            </>)}
                            {cvImaging.length > 0 && (<>
                                <div className="bmch-sub">Abnormal imaging · {cvImaging.length}</div>
                                {cvImaging.map(renderCv)}
                            </>)}
                        </div>
                    </section>
                    )}

                    {/* ──── Care gaps / recommendations / risk factors ──── */}
                    {showInsights && (
                    <section className="bmch-card slate wide">
                        <div className="bmch-card-head">
                            <span className="bmch-card-icon"><Lightbulb /></span>
                            <span className="bmch-card-title">Care insights</span>
                        </div>
                        <div className="bmch-itabs">
                            {[
                                sectionOn("care_gaps") && { id: "gaps", label: "Care gaps", count: careGaps.length },
                                sectionOn("recommendations") && { id: "recs", label: "Recommendations", count: suggestions.length },
                                sectionOn("risk_factors") && { id: "risks", label: "Risk factors", count: riskFactors.length },
                            ].filter(Boolean).map((tab) => (
                                <button type="button" key={tab.id} onClick={() => setInsightsTab(tab.id)} className={`bmch-itab${insightsTab === tab.id ? " on" : ""}`}>
                                    {tab.label}
                                    <span className="bmch-count">{tab.count}</span>
                                </button>
                            ))}
                        </div>
                        <div className="bmch-card-body">
                            {/* Care Gaps tab */}
                            {insightsTab === "gaps" && sectionOn("care_gaps") && (
                                careGaps.length > 0 ? careGaps.map((g, i) => (
                                    <div key={i} className="bmch-row click" onClick={() => setModalItem(g)}>
                                        <span style={{ color: g.type === "urgent" ? BRAND.coral : BRAND.amber, display: "inline-flex", flex: "none" }}><TriangleAlert size={16} /></span>
                                        <span className="bmch-row-text">{g.text || g.description || g.recommendation || "—"}</span>
                                    </div>
                                )) : <div className="bmch-empty" style={{ paddingTop: 10 }}>No care gaps identified</div>
                            )}
                            {/* Recommendations tab */}
                            {insightsTab === "recs" && sectionOn("recommendations") && (
                                suggestions.length > 0 ? suggestions.map((s, i) => {
                                    const sev = severityClass(s.priority || s.severity);
                                    const sevColor = sev === "high" ? BRAND.coral : sev === "low" ? BRAND.emerald : BRAND.amber;
                                    return (
                                        <div key={i} className="bmch-row click" onClick={() => setModalItem(s)}>
                                            <span style={{ color: BRAND.coral, display: "inline-flex", flex: "none" }}><Sparkles size={16} /></span>
                                            <span className="bmch-row-text">{s.recommendation || s.description || "—"}</span>
                                            {(s.priority || s.severity) && <span className="bmch-sev" style={{ color: sevColor }}>{s.priority || s.severity}</span>}
                                        </div>
                                    );
                                }) : <div className="bmch-empty" style={{ paddingTop: 10 }}>No AI recommendations</div>
                            )}
                            {/* Risk Factors tab */}
                            {insightsTab === "risks" && sectionOn("risk_factors") && (
                                riskFactors.length > 0 ? riskFactors.map((r, i) => {
                                    const sev = severityClass(r.severity || r.level);
                                    const sevColor = sev === "high" ? BRAND.coral : sev === "low" ? BRAND.emerald : BRAND.amber;
                                    return (
                                        <div key={i} className="bmch-row click" onClick={() => setModalItem(r)}>
                                            <span className="bmch-row-text">{r.risk || r.area || r.description || "—"}</span>
                                            {(r.severity || r.level) && <span className="bmch-sev" style={{ color: sevColor }}>{r.severity || r.level}</span>}
                                        </div>
                                    );
                                }) : <div className="bmch-empty" style={{ paddingTop: 10 }}>No risk factors identified</div>
                            )}
                        </div>
                    </section>
                    )}
                </div>
                </>)}
            </div>

            {timelinePanel}

            {tlPrefsOpen && (
                <div
                    style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center" }}
                    onClick={() => setTlPrefsOpen(false)}
                >
                    <div className="bmch-prefs" onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                            <span style={{ color: BRAND.coral, display: "inline-flex" }}><Settings size={16} /></span>
                            <span style={{ fontSize: "15px", fontWeight: 600, color: BRAND.navy }}>Timeline documents</span>
                        </div>
                        <p style={{ fontSize: "12.5px", color: BRAND.slateMuted, margin: "0 0 12px", lineHeight: 1.5 }}>
                            Choose which document types appear on your timeline. Saved to your profile — applies wherever you log in.
                        </p>
                        {TIMELINE_DOC_OPTIONS.map((opt) => (
                            <label key={opt.key} style={{ display: "flex", alignItems: "flex-start", gap: "8px", padding: "7px 8px", borderRadius: "8px", cursor: "pointer", background: tlDraft.includes(opt.key) ? BRAND.coralLight : "transparent", marginBottom: "4px" }}>
                                <input
                                    type="checkbox"
                                    checked={tlDraft.includes(opt.key)}
                                    onChange={(e) => setTlDraft(e.target.checked
                                        ? [...tlDraft, opt.key]
                                        : tlDraft.filter((k) => k !== opt.key))}
                                    style={{ marginTop: "3px", accentColor: BRAND.coral }}
                                />
                                <span>
                                    <span style={{ display: "block", fontSize: "13.5px", fontWeight: 500, color: BRAND.navy }}>{opt.label}</span>
                                    <span style={{ display: "block", fontSize: "12px", color: BRAND.slateMuted }}>{opt.hint}</span>
                                </span>
                            </label>
                        ))}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
                            <button type="button" className="bmch-btn ghost" onClick={() => setTlPrefsOpen(false)}>Cancel</button>
                            <button
                                type="button"
                                className="bmch-btn solid"
                                onClick={saveTimelinePrefs}
                                disabled={tlPrefsSaving || tlDraft.length === 0}
                                title={tlDraft.length === 0 ? "Select at least one document type" : undefined}
                            >{tlPrefsSaving ? "Saving…" : "Save"}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modals ── */}
            {modalItem && <DetailModal item={modalItem} onClose={() => setModalItem(null)} />}
            {visitModal && <VisitSummaryModal encounter={visitModal} patientId={patientId}
                patientInfo={{
                    // The chart always knows who this is — the encounter
                    // summary for a same-day/undocumented visit often doesn't
                    // (header fell back to "Unknown Patient", pt 13810 09/02).
                    name: `${dataId?.firstname || ""} ${dataId?.lastname || ""}`.trim(),
                    id: patientId,
                    dob: dataId?.dob || "",
                }}
                onClose={() => setVisitModal(null)} />}
            {labModal && <LabsMiniModal labGroup={labModal} onClose={() => setLabModal(null)} onSwitchToLabs={handleSwitchToLabs} />}
            {
                docModalOpen && docModal && (
                    <ProcedureDocument
                        isProcedureDocumentOpen={docModalOpen}
                        setIsProcedureDocumentOpen={setDocModalOpen}
                        procedure={docModal}
                        subview={1}
                    />
                )
            }
        </div>
    );
}

function isOutOfRange(value, refRange) {
    if (!value || !refRange) return false;
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return false;
    const cleaned = refRange.replace(/\s/g, "");
    if (cleaned.includes("-")) {
        const [min, max] = cleaned.split("-").map(Number);
        if (isNaN(min) || isNaN(max)) return false;
        return numericValue < min || numericValue > max;
    }
    if (cleaned.startsWith("<=")) return numericValue > Number(cleaned.replace("<=", ""));
    if (cleaned.startsWith(">=")) return numericValue < Number(cleaned.replace(">=", ""));
    return false;
}
